export type LanguageCode = 'en' | 'hi' | 'te' | 'ta' | 'kn' | 'bn' | 'ml' | 'mr';

export const languages: { code: LanguageCode; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'mr', label: 'मराठी' },
];

type Dict = Record<string, string>;

const en: Dict = {
  // Common
  save: 'Save',
  save_changes: 'Save Changes',
  cancel: 'Cancel',
  delete: 'Delete',
  edit: 'Edit',
  add: 'Add',
  search: 'Search',
  close: 'Close',
  loading: 'Loading…',
  saving: 'Saving…',
  refresh: 'Refresh',
  back: 'Back',
  continue: 'Continue',
  enroll: 'Enroll',
  enrolling: 'Enrolling…',
  status_enrolled: 'Enrolled',
  download: 'Download',
  sign_out: 'Sign Out',
  signing_out: 'Signing out…',
  my_profile: 'My Profile',
  settings: 'Settings',

  // Sidebar — Admin
  nav_dashboard: 'Dashboard',
  nav_reports: 'Reports',
  nav_courses: 'Courses',
  nav_course_management: 'Course Management',
  nav_media: 'Videos & Media',
  nav_assessments: 'Assessments',
  nav_members: 'Members',
  nav_trainers: 'Trainers',
  nav_certificates: 'Certificates',
  nav_notifications: 'Notifications',
  nav_settings: 'Settings',
  // Sidebar — Trainer
  nav_learner_progress: 'Learner Progress',
  // Sidebar — Learner
  nav_my_dashboard: 'My Dashboard',
  nav_my_courses: 'My Courses',
  nav_help: 'Help',
  group_overview: 'Overview',
  group_content: 'Content',
  group_people: 'People',
  group_system: 'System',
  group_my_work: 'My Work',
  group_learning: 'Learning',
  group_support: 'Support',

  // Topbar
  role_admin: 'Admin — FEED HQ',
  role_trainer: 'Field Trainer',
  role_learner: 'Learner',

  // Login page
  login_welcome_back: 'Welcome back',
  login_create_account: 'Create account',
  login_signin_subtitle: 'Sign in to continue your financial literacy journey',
  login_signup_subtitle: 'Join the RBI Kehtaa Hai training program',
  login_i_am_a: 'I am a',
  login_role_learner: 'Learner',
  login_role_trainer: 'Field Trainer',
  login_role_admin: 'Admin',
  login_full_name: 'Full Name',
  login_email: 'Email Address',
  login_password: 'Password',
  login_sign_in: 'Sign In',
  login_sign_up: 'Create Account',
  login_signing_in: 'Signing in…',
  login_creating_account: 'Creating account…',
  login_no_account: "Don't have an account? ",
  login_have_account: 'Already have an account? ',
  login_toggle_signup: 'Sign up',
  login_toggle_signin: 'Sign in',
  login_demo_accounts: '🔒 Demo Accounts — Click to autofill',
  login_hero_tagline: 'RBI Kehtaa Hai Campaign',
  login_hero_desc: 'Financial literacy training for learners, community members, and field staff — OTP fraud, UPI safety, loan fraud, and cyber security.',

  // Admin dashboard
  admin_dashboard_title: 'Training Impact Dashboard',
  admin_dashboard_subtitle: 'RBI Kehtaa Hai — FEED & CMS Foundation',
  export_report: 'Export Report',
  filter: 'Filter',
  this_week: 'This Week',
  this_month: 'This Month',
  last_3_months: 'Last 3 Months',
  all_time: 'All Time',
  kpi_members_trained: 'Members Trained',
  kpi_members_trained_sub: 'Total registered learners',
  kpi_certificates_issued: 'Certificates Issued',
  kpi_all_time: 'All time',
  kpi_members_pending: 'Members Pending',
  kpi_members_pending_sub: 'Not yet enrolled',
  kpi_pass_rate: 'Overall Pass Rate',
  kpi_districts_covered: 'Districts Covered',
  kpi_districts_covered_sub: 'Across all learners',
  kpi_active_courses: 'Active Courses',
  kpi_active_courses_sub: 'Published & available',

  // Trainer dashboard
  trainer_dashboard_title: 'Trainer Dashboard',
  trainer_courses_created: 'Courses Created',
  trainer_total_learners: 'Total Learners',
  trainer_avg_completion: 'Avg Completion',
  trainer_certs_issued: 'Certificates Issued',

  // Learner dashboard
  learner_namaste: 'Namaste',
  learner_dashboard_subtitle: 'Continue your RBI Kehtaa Hai financial literacy training',
  learner_courses_enrolled: 'Courses Enrolled',
  learner_modules_completed: 'Modules Completed',
  learner_avg_quiz_score: 'Average Quiz Score',
  learner_certificates_earned: 'Certificates Earned',
  my_courses_title: 'My Courses',
  my_courses_subtitle: 'Continue where you left off, or enroll in a new course below',
  browse_courses: 'Browse Courses',
  continue_course: 'Continue Course',
  take_final_quiz: 'Take Final Quiz',

  // Course content
  lessons: 'Lessons',
  topics: 'Topics',
  course_progress: 'Course Progress',
  mark_complete: 'Mark Complete',
  completed: 'Completed',
  previous: 'Previous',
  next: 'Next',
  enroll_now: 'Enroll Now',
  take_topic_quiz: 'Take Topic Quiz',
  topic_quiz_passed: 'Quiz Passed',

  // Quiz modal
  quiz_loading: 'Loading Quiz…',
  quiz_fetching_questions: 'Fetching questions from course…',
  quiz_title: 'Quiz',
  quiz_no_questions_course: 'No quiz questions available for this course yet.',
  quiz_no_questions_topic: 'No quiz questions available for this topic yet.',
  quiz_results_title: 'Quiz Results',
  quiz_question_prefix: 'Quiz — Question',
  of: 'of',
  financial_literacy_quiz: 'Financial Literacy Quiz',
  show_hint: 'Show hint',
  hide_explanation: 'Hide explanation',
  submit_quiz: 'Submit Quiz',
  select_answer_warning: 'Please select an answer before continuing',
  you_answered: 'You answered',
  out_of: 'out of',
  correctly: 'correctly',
  quiz_congrats: 'Congratulations! You passed! 🎉',
  quiz_cert_generated_note: 'Your certificate has been generated. Download it from the Certificates panel.',
  quiz_need_80: 'You need ≥80% to earn a certificate',
  quiz_review_retry: 'Review the material and try again to earn your certificate.',
  correct_answer_label: 'Correct:',
  retry: 'Retry',
  done: 'Done',

  // Certificates panel
  my_certificates: 'My Certificates',
  pending_certificates: 'Pending Certificates',
  download_pdf: 'Download PDF',
  generating: 'Generating…',
  no_certificates_yet: 'No certificates yet',
  score_80_hint: 'Score ≥80% on a quiz to earn one',
  not_attempted: 'Not attempted',
  need_label: 'Need',
  ready_label: 'Ready!',
  score_label: 'Score',
  certificate_for: 'Certificate for',
  downloaded_suffix: 'downloaded!',
  cert_download_failed: 'Failed to generate certificate. Please try again.',
  course_fallback: 'Course',

  // Active module player
  no_courses_enrolled: 'No courses enrolled yet',
  no_courses_enrolled_desc: 'Enrolled courses will appear here so you can continue learning',
  continue_learning: 'Continue learning',
  modules_label: 'modules',

  // Course list panel
  enrolled_in_prefix: 'Enrolled in',
  enrollment_failed: 'Enrollment failed',
  view_course: 'View course',

  // Learner KPI cards
  kpi_need_80_certify: '≥80% to certify',
  kpi_no_attempts_yet: 'No attempts yet',
  courses_pending_suffix: 'course(s) pending',

  // Course content page
  module_marked_complete: 'Module marked as complete!',
  failed_update_progress: 'Failed to update progress',
  no_downloadable_content: 'No downloadable content available for this module',
  download_started: 'Download started!',
  download_failed_retry: 'Download failed. Please try again.',
  enrolled_loading_course: 'Enrolled! Loading course…',
  topic_quiz_passed_toast: 'Topic quiz passed!',
  score_saved_prefix: 'Score saved',
  need_80_to_pass: 'Need ≥80% to pass.',
  failed_save_quiz_results: 'Failed to save quiz results',
  score_saved_cert_generated: 'Score saved! Certificate generated for',
  need_80_for_cert: 'Need ≥80% for certificate.',
  no_course_selected: 'No course selected. Please go back and select a course.',
  back_to_dashboard: 'Back to Dashboard',
  need_enroll_message: 'You need to enroll in this course before you can watch its videos or take its quiz.',
  lessons_completed_suffix: 'lessons completed',
  no_lessons_uploaded: 'No lessons uploaded yet',
  min_suffix: 'min',
  no_media_uploaded: 'No media uploaded for this lesson',
  admin_trainer_upload_hint: 'Admin/trainer can upload video/PDF from the course management panel',
  select_module_to_start: 'Select a module to start learning',
  video_label: 'Video',
  pdf_label: 'PDF',
  browser_no_video_support: 'Your browser does not support the video tag.',

  // Profile settings form
  profile_email: 'Email',
  profile_full_name: 'Full Name',
  profile_phone: 'Phone',
  profile_village: 'Village',
  profile_district: 'District',
  profile_age: 'Age',
  profile_gender: 'Gender',
  profile_city: 'City',
  profile_language: 'Language',
  profile_updated_toast: 'Profile updated',
  profile_update_failed: 'Failed to update profile',

  // Login / signup page
  welcome_back_signing_in_as: 'Welcome back! Signing you in as',
  account_created_signing_in: 'Account created! Signing you in…',
  authentication_failed: 'Authentication failed',
  invalid_credentials_use_demo: 'Invalid email or password. Use the demo accounts below.',
  email_already_registered: 'This email is already registered. Please sign in instead.',
  topic_otp_fraud: 'OTP Fraud',
  topic_upi_safety: 'UPI Safety',
  topic_loan_fraud: 'Loan Fraud',
  topic_cyber_safety: 'Cyber Safety',
  topic_kyc_basics: 'KYC Basics',
  topic_digital_banking: 'Digital Banking',
  placeholder_min_8_chars: 'Min 8 characters',
  placeholder_your_password: 'Your password',
  password_min_length_error: 'Password must be at least 8 characters',
  password_required_error: 'Password is required',
  placeholder_age_example: 'e.g. 28',
  placeholder_your_city: 'Your city',
  placeholder_mobile_number: '10-digit mobile number',
  select_placeholder: 'Select',
  gender_male: 'Male',
  gender_female: 'Female',
  gender_other: 'Other',
  quiz_results_save_error: 'Something went wrong saving your quiz results.',

  // Global chrome (Topbar/Sidebar/ThemeToggle)
  signed_out_success: 'Signed out successfully',
  sign_out_failed: 'Sign out failed',
  expand_sidebar: 'Expand sidebar',
  collapse_sidebar: 'Collapse sidebar',
  switch_to_light_mode: 'Switch to light mode',
  switch_to_dark_mode: 'Switch to dark mode',
  placeholder_your_full_name: 'Your full name',
  mobile_number_label: 'Mobile Number',
  placeholder_email_example: 'you@example.com',
  copy_password_tooltip: 'Copy password',

  // Admin dashboard — our web properties
  our_properties_title: 'Our Web Properties',
  property_main_site_label: 'FEED Foundation',
  property_main_site_full_title: 'The Foundation for Equality, Equity and Dignity',
  property_flc_site_label: 'CMS-FEED FLC',
  property_flc_site_full_title: 'CMS-FEED Financial Literacy Centre',

  // Login form — email or mobile number
  login_email_or_phone: 'Email or Mobile Number',
  placeholder_email_or_phone: 'you@example.com or 10-digit number',
  login_email_required_error: 'Email is required',
  login_identifier_required_error: 'Email or mobile number is required',
  phone_invalid_error: 'Enter a valid 10-digit mobile number',
  phone_required_error: 'Mobile number is required',
  // Added: previously missing / newly extracted strings
  login_forgot_password: 'Forgot password?',
  fp_title: 'Forgot password',
  fp_subtitle: 'Enter your email or mobile number and we will email you a 6-digit OTP.',
  fp_identifier_placeholder: 'Email or mobile number',
  fp_send_otp: 'Send OTP',
  fp_otp_sent: 'If an account exists, an OTP has been sent to its email. Enter it below with your new password. The OTP is valid for 10 minutes.',
  fp_otp_placeholder: '6-digit OTP',
  fp_new_password: 'New password (min 8 characters)',
  fp_confirm_password: 'Confirm new password',
  fp_reset_password: 'Reset password',
  fp_resend_otp: 'Resend OTP',
  fp_done: 'Your password has been updated.',
  fp_pw_min: 'Password must be at least 8 characters.',
  fp_pw_mismatch: 'Passwords do not match.',
  fp_back_to_signin: 'Back to sign in',
  not_found_title: 'Page Not Found',
  not_found_desc: 'The page you\'re looking for doesn\'t exist. Let\'s get you back!',
  go_back: 'Go Back',
  back_to_home: 'Back to Home',
  trainer_my_courses: 'My Courses',
  trainer_courses_by_you: 'course(s) created by you',
  trainer_add_course: 'Add Course',
  trainer_modules_count: 'module(s)',
  trainer_manage_modules: 'Manage videos/modules',
  trainer_edit_course: 'Edit course',
  trainer_delete_course: 'Delete course',
  trainer_completion_overview: 'Course Completion Overview',
  login_initiative: 'A joint initiative by FEED — Foundation for Equality, Equity and Dignity & CMS Foundation',
};

const hi: Dict = {
  save: 'सहेजें',
  save_changes: 'बदलाव सहेजें',
  cancel: 'रद्द करें',
  delete: 'हटाएं',
  edit: 'संपादित करें',
  add: 'जोड़ें',
  search: 'खोजें',
  close: 'बंद करें',
  loading: 'लोड हो रहा है…',
  saving: 'सहेजा जा रहा है…',
  refresh: 'रिफ़्रेश करें',
  back: 'वापस',
  continue: 'जारी रखें',
  enroll: 'नामांकन करें',
  enrolling: 'नामांकन हो रहा है…',
  status_enrolled: 'नामांकित',
  download: 'डाउनलोड',
  sign_out: 'साइन आउट',
  signing_out: 'साइन आउट हो रहा है…',
  my_profile: 'मेरी प्रोफ़ाइल',
  settings: 'सेटिंग्स',

  nav_dashboard: 'डैशबोर्ड',
  nav_reports: 'रिपोर्ट्स',
  nav_courses: 'कोर्स',
  nav_course_management: 'कोर्स प्रबंधन',
  nav_media: 'वीडियो और मीडिया',
  nav_assessments: 'मूल्यांकन',
  nav_members: 'सदस्य',
  nav_trainers: 'प्रशिक्षक',
  nav_certificates: 'प्रमाणपत्र',
  nav_notifications: 'सूचनाएं',
  nav_settings: 'सेटिंग्स',
  nav_learner_progress: 'शिक्षार्थी प्रगति',
  nav_my_dashboard: 'मेरा डैशबोर्ड',
  nav_my_courses: 'मेरे कोर्स',
  nav_help: 'सहायता',
  group_overview: 'अवलोकन',
  group_content: 'सामग्री',
  group_people: 'लोग',
  group_system: 'सिस्टम',
  group_my_work: 'मेरा काम',
  group_learning: 'सीखना',
  group_support: 'सहायता',

  role_admin: 'एडमिन — FEED मुख्यालय',
  role_trainer: 'फील्ड प्रशिक्षक',
  role_learner: 'शिक्षार्थी',

  login_welcome_back: 'वापसी पर स्वागत है',
  login_create_account: 'खाता बनाएं',
  login_signin_subtitle: 'अपनी वित्तीय साक्षरता यात्रा जारी रखने के लिए साइन इन करें',
  login_signup_subtitle: 'RBI कहता है प्रशिक्षण कार्यक्रम से जुड़ें',
  login_i_am_a: 'मैं हूं',
  login_role_learner: 'शिक्षार्थी',
  login_role_trainer: 'फील्ड प्रशिक्षक',
  login_role_admin: 'एडमिन',
  login_full_name: 'पूरा नाम',
  login_email: 'ईमेल पता',
  login_password: 'पासवर्ड',
  login_sign_in: 'साइन इन करें',
  login_sign_up: 'खाता बनाएं',
  login_signing_in: 'साइन इन हो रहा है…',
  login_creating_account: 'खाता बनाया जा रहा है…',
  login_no_account: 'खाता नहीं है? ',
  login_have_account: 'पहले से खाता है? ',
  login_toggle_signup: 'साइन अप करें',
  login_toggle_signin: 'साइन इन करें',
  login_demo_accounts: '🔒 डेमो खाते — भरने के लिए क्लिक करें',
  login_hero_tagline: 'RBI कहता है अभियान',
  login_hero_desc: 'शिक्षार्थियों, सामुदायिक सदस्यों और फील्ड स्टाफ के लिए वित्तीय साक्षरता प्रशिक्षण — OTP धोखाधड़ी, UPI सुरक्षा, ऋण धोखाधड़ी और साइबर सुरक्षा।',

  admin_dashboard_title: 'प्रशिक्षण प्रभाव डैशबोर्ड',
  admin_dashboard_subtitle: 'RBI कहता है — FEED और CMS फाउंडेशन',
  export_report: 'रिपोर्ट निर्यात करें',
  filter: 'फ़िल्टर',
  this_week: 'इस सप्ताह',
  this_month: 'इस महीने',
  last_3_months: 'पिछले 3 महीने',
  all_time: 'सभी समय',
  kpi_members_trained: 'प्रशिक्षित सदस्य',
  kpi_members_trained_sub: 'कुल पंजीकृत शिक्षार्थी',
  kpi_certificates_issued: 'जारी प्रमाणपत्र',
  kpi_all_time: 'सभी समय',
  kpi_members_pending: 'लंबित सदस्य',
  kpi_members_pending_sub: 'अभी तक नामांकित नहीं',
  kpi_pass_rate: 'कुल उत्तीर्ण दर',
  kpi_districts_covered: 'कवर किए गए जिले',
  kpi_districts_covered_sub: 'सभी शिक्षार्थियों में',
  kpi_active_courses: 'सक्रिय कोर्स',
  kpi_active_courses_sub: 'प्रकाशित और उपलब्ध',

  trainer_dashboard_title: 'प्रशिक्षक डैशबोर्ड',
  trainer_courses_created: 'बनाए गए कोर्स',
  trainer_total_learners: 'कुल शिक्षार्थी',
  trainer_avg_completion: 'औसत पूर्णता',
  trainer_certs_issued: 'जारी प्रमाणपत्र',

  learner_namaste: 'नमस्ते',
  learner_dashboard_subtitle: 'अपनी RBI कहता है वित्तीय साक्षरता प्रशिक्षण जारी रखें',
  learner_courses_enrolled: 'नामांकित कोर्स',
  learner_modules_completed: 'पूर्ण मॉड्यूल',
  learner_avg_quiz_score: 'औसत क्विज़ स्कोर',
  learner_certificates_earned: 'अर्जित प्रमाणपत्र',
  my_courses_title: 'मेरे कोर्स',
  my_courses_subtitle: 'जहां से छोड़ा था वहीं से जारी रखें, या नीचे नए कोर्स में नामांकन करें',
  browse_courses: 'कोर्स ब्राउज़ करें',
  continue_course: 'कोर्स जारी रखें',
  take_final_quiz: 'अंतिम क्विज़ दें',

  lessons: 'पाठ',
  topics: 'विषय',
  course_progress: 'कोर्स प्रगति',
  mark_complete: 'पूर्ण चिह्नित करें',
  completed: 'पूर्ण',
  previous: 'पिछला',
  next: 'अगला',
  enroll_now: 'अभी नामांकन करें',
  take_topic_quiz: 'विषय क्विज़ दें',
  topic_quiz_passed: 'क्विज़ उत्तीर्ण',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'प्रश्नोत्तरी लोड हो रही है',
  quiz_fetching_questions: 'पाठ्यक्रम से प्रश्न लाये जा रहे हैं',
  quiz_title: 'प्रश्नोत्तरी',
  quiz_no_questions_course: 'इस पाठ्यक्रम के लिए अभी तक कोई प्रश्नोत्तरी प्रश्न उपलब्ध नहीं है।',
  quiz_no_questions_topic: 'इस विषय के लिए अभी तक कोई प्रश्नोत्तरी प्रश्न उपलब्ध नहीं है।',
  quiz_results_title: 'प्रश्नोत्तरी परिणाम',
  quiz_question_prefix: 'प्रश्नोत्तरी, प्रश्न',
  of: 'का',
  financial_literacy_quiz: 'वित्तीय साक्षरता प्रश्नोत्तरी.',
  show_hint: 'संकेत दिखाएँ',
  hide_explanation: 'स्पष्टीकरण छिपाएँ',
  submit_quiz: 'प्रश्नोत्तरी सबमिट करें.',
  select_answer_warning: 'कृपया जारी रखने से पहले एक उत्तर चुनें.',
  you_answered: 'आपने उत्तर दिया',
  out_of: 'से बाहर',
  correctly: 'सही ढंग से',
  quiz_congrats: 'बधाई हो! आपने पारित किया!',
  quiz_cert_generated_note: 'आपका प्रमाणपत्र तैयार हो गया है. इसे प्रमाणपत्र पैनल से डाउनलोड करें.',
  quiz_need_80: 'प्रमाणपत्र अर्जित करने के लिए आपको कम से कम 80 प्रतिशत की आवश्यकता है',
  quiz_review_retry: 'सामग्री की समीक्षा करें और अपना प्रमाणपत्र अर्जित करने के लिए पुनः प्रयास करें।',
  correct_answer_label: 'सही',
  retry: 'पुन: प्रयास करें',
  done: 'हो गया',
  my_certificates: 'मेरे प्रमाणपत्र',
  pending_certificates: 'लंबित प्रमाणपत्र',
  download_pdf: 'पीडीएफ डाउनलोड करें',
  generating: 'उत्पादक',
  no_certificates_yet: 'अभी तक कोई प्रमाणपत्र नहीं.',
  score_80_hint: 'क्विज़ कमाने के लिए क्विज़ में कम से कम 80 प्रतिशत अंक प्राप्त करें।',
  not_attempted: 'प्रयास नहीं किया गया.',
  need_label: 'ज़रूरत।',
  ready_label: 'तैयार!',
  score_label: 'अंक',
  certificate_for: 'के लिए प्रमाण पत्र.',
  downloaded_suffix: 'डाउनलोड किया गया!',
  cert_download_failed: 'प्रमाणपत्र बनाने में विफल. कृपया पुन: प्रयास करें।',
  course_fallback: 'अवधि',
  no_courses_enrolled: 'अभी तक किसी पाठ्यक्रम में नामांकन नहीं हुआ है',
  no_courses_enrolled_desc: 'नामांकित पाठ्यक्रम यहां दिखाई देंगे ताकि आप सीखना जारी रख सकें',
  continue_learning: 'सीखना जारी रखें',
  modules_label: 'मॉड्यूल',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'नामांकन कराना',
  enrollment_failed: 'नामांकन विफल रहा',
  view_course: 'पाठ्यक्रम देखें',
  kpi_need_80_certify: 'प्रमाणित करने के लिए कम से कम 80 प्रतिशत',
  kpi_no_attempts_yet: 'अभी तक कोई प्रयास नहीं',
  courses_pending_suffix: 'पाठ्यक्रम लंबित हैं',
  module_marked_complete: 'मॉड्यूल पूर्ण के रूप में चिह्नित!',
  failed_update_progress: 'प्रगति अद्यतन करने में विफल.',
  no_downloadable_content: 'इस मॉड्यूल के लिए कोई डाउनलोड करने योग्य सामग्री उपलब्ध नहीं है।',
  download_started: 'डाउनलोड प्रारंभ हुआ!',
  download_failed_retry: 'डाउनलोड विफल रहा. कृपया पुन: प्रयास करें।',
  enrolled_loading_course: 'दाखिला लिया! लोड हो रहा है पाठ्यक्रम.',
  topic_quiz_passed_toast: 'विषय प्रश्नोत्तरी उत्तीर्ण!',
  score_saved_prefix: 'स्कोर सहेजा गया',
  need_80_to_pass: 'उत्तीर्ण होने के लिए कम से कम 80 प्रतिशत अंक चाहिए।',
  failed_save_quiz_results: 'प्रश्नोत्तरी परिणाम सहेजने में विफल.',
  score_saved_cert_generated: 'स्कोर सहेजा गया! के लिए प्रमाणपत्र तैयार किया गया',
  need_80_for_cert: 'सर्टिफिकेट के लिए कम से कम 80 फीसदी अंक चाहिए.',
  no_course_selected: 'कोई पाठ्यक्रम चयनित नहीं. कृपया वापस जाएँ और एक पाठ्यक्रम चुनें।',
  back_to_dashboard: 'डैशबोर्ड पर वापस जाएँ',
  need_enroll_message: 'इससे पहले कि आप इसके वीडियो देख सकें या इसकी प्रश्नोत्तरी ले सकें, आपको इस पाठ्यक्रम में नामांकन करना होगा।',
  lessons_completed_suffix: 'पाठ पूरा हुआ.',
  no_lessons_uploaded: 'अभी तक कोई पाठ अपलोड नहीं किया गया',
  min_suffix: 'मि.',
  no_media_uploaded: 'इस पाठ के लिए कोई मीडिया अपलोड नहीं किया गया',
  admin_trainer_upload_hint: 'व्यवस्थापक/प्रशिक्षक पाठ्यक्रम प्रबंधन पैनल से वीडियो/पीडीएफ अपलोड कर सकते हैं',
  select_module_to_start: 'सीखना शुरू करने के लिए एक मॉड्यूल चुनें',
  video_label: 'वीडियो',
  pdf_label: 'पीडीएफ.',
  browser_no_video_support: 'आपका ब्राउज़र में वीडियो टैग समर्थित नहीं है।',
  profile_email: 'ईमेल',
  profile_full_name: 'पूरा नाम।',
  profile_phone: 'फ़ोन',
  profile_village: 'गाँव',
  profile_district: 'ज़िला।',
  profile_age: 'आयु।',
  profile_gender: 'लिंग',
  profile_city: 'शहर',
  profile_language: 'भाषा।',
  profile_updated_toast: 'प्रोफाइल अद्यतन किया गया।',
  profile_update_failed: 'प्रोफ़ाइल अपडेट करने में विफल',
  welcome_back_signing_in_as: 'वापसी पर स्वागत है! आपको इस रूप में साइन इन किया जा रहा है',
  account_created_signing_in: 'खाता बनाया गया! आपको साइन इन किया जा रहा है',
  authentication_failed: 'प्रमाणीकरण विफल होना',
  invalid_credentials_use_demo: 'अमान्य ईमेल या पासवर्ड. नीचे दिए गए डेमो खातों का उपयोग करें।',
  email_already_registered: 'यह ईमेल पहले से ही पंजीकृत है. कृपया इसके बजाय साइन इन करें.',
  topic_otp_fraud: 'ओटीपी धोखाधड़ी',
  topic_upi_safety: 'यूपीआई सुरक्षा',
  topic_loan_fraud: 'ऋण धोखाधड़ी',
  topic_cyber_safety: 'साइबर सुरक्षा',
  topic_kyc_basics: 'केवाईसी मूल बातें',
  topic_digital_banking: 'डिजिटल बैंकिंग',
  placeholder_min_8_chars: 'न्यूनतम 8 अक्षर.',
  placeholder_your_password: 'आपका पासवर्ड',
  password_min_length_error: 'पासवर्ड कम से कम 8 वर्णों का होना चाहिए।',
  password_required_error: 'पासवर्ड आवश्यक है.',
  placeholder_age_example: 'जैसे 28',
  placeholder_your_city: 'आपका सिटि',
  placeholder_mobile_number: '10 अंकों का मोबाइल नंबर',
  select_placeholder: 'चुनना',
  gender_male: 'पुरुष।',
  gender_female: 'महिला',
  gender_other: 'अन्य',
  quiz_results_save_error: 'आपके प्रश्नोत्तरी परिणामों को सहेजते समय कुछ गलत हो गया।',
  signed_out_success: 'सफलतापूर्वक साइन आउट हो गया',
  sign_out_failed: 'साइन आउट विफल रहा.',
  expand_sidebar: 'साइडबार का विस्तार करें',
  collapse_sidebar: 'साइडबार को संक्षिप्त करें.',
  switch_to_light_mode: 'लाइट मोड पर स्विच करें.',
  switch_to_dark_mode: 'डार्क मोड पर स्विच करें.',


  // Signup form additions
  placeholder_your_full_name: 'आपका पूरा नाम',
  mobile_number_label: 'मोबाइल नंबर',
  placeholder_email_example: 'you@example.com',
  copy_password_tooltip: 'पासवर्ड कॉपी करें',


  // Admin dashboard — our web properties
  our_properties_title: 'हमारी वेब संपत्तियाँ',

  property_main_site_label: 'फ़ीड फाउंडेशन',
  property_main_site_full_title: 'समानता, समता और गरिमा का आधार',

  property_flc_site_label: 'सीएमएस-फीड एफएलसी।',
  property_flc_site_full_title: 'सीएमएस-फ़ीड वित्तीय साक्षरता केंद्र',

  login_email_or_phone: 'ईमेल या मोबाइल नंबर',
  placeholder_email_or_phone: 'you@example.com या 10 अंकों की संख्या',
  login_email_required_error: 'ईमेल की जरूरत है',
  login_identifier_required_error: 'ईमेल या मोबाइल नंबर आवश्यक है',

  phone_invalid_error: 'वैध 10 अंकों का मोबाइल नंबर दर्ज करें।',
  phone_required_error: 'मोबाइल नंबर आवश्यक है.',

  // Added: previously missing / newly extracted strings
  login_forgot_password: 'पासवर्ड भूल गए?',
  fp_title: 'पासवर्ड भूल गए',
  fp_subtitle: 'अपना ईमेल या मोबाइल नंबर दर्ज करें, हम आपको 6 अंकों का OTP ईमेल करेंगे।',
  fp_identifier_placeholder: 'ईमेल या मोबाइल नंबर',
  fp_send_otp: 'OTP भेजें',
  fp_otp_sent: 'यदि खाता मौजूद है, तो उसके ईमेल पर OTP भेजा गया है। इसे नए पासवर्ड के साथ नीचे दर्ज करें। OTP 10 मिनट के लिए मान्य है।',
  fp_otp_placeholder: '6 अंकों का OTP',
  fp_new_password: 'नया पासवर्ड (कम से कम 8 अक्षर)',
  fp_confirm_password: 'नए पासवर्ड की पुष्टि करें',
  fp_reset_password: 'पासवर्ड रीसेट करें',
  fp_resend_otp: 'OTP दोबारा भेजें',
  fp_done: 'आपका पासवर्ड अपडेट हो गया है।',
  fp_pw_min: 'पासवर्ड कम से कम 8 अक्षरों का होना चाहिए।',
  fp_pw_mismatch: 'पासवर्ड मेल नहीं खाते।',
  fp_back_to_signin: 'साइन इन पर वापस जाएं',
  not_found_title: 'पेज नहीं मिला',
  not_found_desc: 'आप जो पेज ढूंढ रहे हैं वह मौजूद नहीं है। चलिए आपको वापस ले चलते हैं!',
  go_back: 'वापस जाएं',
  back_to_home: 'होम पर वापस जाएं',
  trainer_my_courses: 'मेरे कोर्स',
  trainer_courses_by_you: 'आपके द्वारा बनाए गए कोर्स',
  trainer_add_course: 'कोर्स जोड़ें',
  trainer_modules_count: 'मॉड्यूल',
  trainer_manage_modules: 'वीडियो/मॉड्यूल प्रबंधित करें',
  trainer_edit_course: 'कोर्स संपादित करें',
  trainer_delete_course: 'कोर्स हटाएं',
  trainer_completion_overview: 'कोर्स पूर्णता अवलोकन',
  login_initiative: 'FEED — फाउंडेशन फॉर इक्वालिटी, इक्विटी एंड डिग्निटी और CMS फाउंडेशन की संयुक्त पहल',
};

const te: Dict = {
  save: 'సేవ్ చేయండి', save_changes: 'మార్పులను సేవ్ చేయండి', cancel: 'రద్దు చేయండి', delete: 'తొలగించండి',
  edit: 'సవరించండి', add: 'జోడించండి', search: 'వెతకండి', close: 'మూసివేయండి', loading: 'లోడ్ అవుతోంది…',
  saving: 'సేవ్ అవుతోంది…', refresh: 'రిఫ్రెష్ చేయండి', back: 'వెనుకకు', continue: 'కొనసాగించండి',
  enroll: 'నమోదు చేసుకోండి', enrolling: 'నమోదు అవుతోంది…', download: 'డౌన్‌లోడ్', sign_out: 'సైన్ అవుట్',
  signing_out: 'సైన్ అవుట్ అవుతోంది…', my_profile: 'నా ప్రొఫైల్', settings: 'సెట్టింగ్‌లు',
  nav_dashboard: 'డాష్‌బోర్డ్', nav_reports: 'నివేదికలు', nav_courses: 'కోర్సులు', nav_course_management: 'కోర్సు నిర్వహణ',
  nav_media: 'వీడియోలు & మీడియా', nav_assessments: 'మూల్యాంకనాలు', nav_members: 'సభ్యులు', nav_trainers: 'శిక్షకులు',
  nav_certificates: 'ధృవపత్రాలు', nav_notifications: 'నోటిఫికేషన్‌లు', nav_settings: 'సెట్టింగ్‌లు',
  nav_learner_progress: 'అభ్యాసకుల పురోగతి', nav_my_dashboard: 'నా డాష్‌బోర్డ్', nav_my_courses: 'నా కోర్సులు',
  nav_help: 'సహాయం', group_overview: 'అవలోకనం', group_content: 'కంటెంట్', group_people: 'వ్యక్తులు',
  group_system: 'సిస్టమ్', group_my_work: 'నా పని', group_learning: 'నేర్చుకోవడం', group_support: 'మద్దతు',
  role_admin: 'అడ్మిన్ — FEED HQ', role_trainer: 'ఫీల్డ్ శిక్షకుడు', role_learner: 'అభ్యాసకుడు',
  login_welcome_back: 'తిరిగి స్వాగతం', login_create_account: 'ఖాతా సృష్టించండి',
  login_signin_subtitle: 'మీ ఆర్థిక అక్షరాస్యత ప్రయాణాన్ని కొనసాగించడానికి సైన్ ఇన్ చేయండి',
  login_signup_subtitle: 'RBI కెహతా హై శిక్షణ కార్యక్రమంలో చేరండి', login_i_am_a: 'నేను ఒక',
  login_role_learner: 'అభ్యాసకుడు', login_role_trainer: 'ఫీల్డ్ శిక్షకుడు', login_role_admin: 'అడ్మిన్',
  login_full_name: 'పూర్తి పేరు', login_email: 'ఇమెయిల్ చిరునామా', login_password: 'పాస్‌వర్డ్',
  login_sign_in: 'సైన్ ఇన్', login_sign_up: 'ఖాతా సృష్టించండి', login_signing_in: 'సైన్ ఇన్ అవుతోంది…',
  login_creating_account: 'ఖాతా సృష్టిస్తోంది…', login_no_account: 'ఖాతా లేదా? ', login_have_account: 'ఇప్పటికే ఖాతా ఉందా? ',
  login_toggle_signup: 'సైన్ అప్', login_toggle_signin: 'సైన్ ఇన్', login_demo_accounts: '🔒 డెమో ఖాతాలు — నింపడానికి క్లిక్ చేయండి',
  login_hero_tagline: 'RBI కెహతా హై ప్రచారం',
  login_hero_desc: 'అభ్యాసకులు, సముదాయ సభ్యులు మరియు ఫీల్డ్ సిబ్బంది కోసం ఆర్థిక అక్షరాస్యత శిక్షణ — OTP మోసం, UPI భద్రత, రుణ మోసం మరియు సైబర్ భద్రత.',
  admin_dashboard_title: 'శిక్షణ ప్రభావ డాష్‌బోర్డ్', admin_dashboard_subtitle: 'RBI కెహతా హై — FEED & CMS ఫౌండేషన్',
  export_report: 'నివేదికను ఎగుమతి చేయండి', filter: 'ఫిల్టర్', this_week: 'ఈ వారం', this_month: 'ఈ నెల',
  last_3_months: 'గత 3 నెలలు', all_time: 'అన్ని సమయాలు', kpi_members_trained: 'శిక్షణ పొందిన సభ్యులు',
  kpi_members_trained_sub: 'మొత్తం నమోదైన అభ్యాసకులు', kpi_certificates_issued: 'జారీ చేసిన ధృవపత్రాలు',
  kpi_all_time: 'అన్ని సమయాలు', kpi_members_pending: 'పెండింగ్ సభ్యులు', kpi_members_pending_sub: 'ఇంకా నమోదు కాలేదు',
  kpi_pass_rate: 'మొత్తం ఉత్తీర్ణత రేటు', kpi_districts_covered: 'కవర్ చేసిన జిల్లాలు', kpi_districts_covered_sub: 'అందరు అభ్యాసకులలో',
  kpi_active_courses: 'క్రియాశీల కోర్సులు', kpi_active_courses_sub: 'ప్రచురించబడి అందుబాటులో ఉంది',
  trainer_dashboard_title: 'శిక్షకుల డాష్‌బోర్డ్', trainer_courses_created: 'సృష్టించిన కోర్సులు',
  trainer_total_learners: 'మొత్తం అభ్యాసకులు', trainer_avg_completion: 'సగటు పూర్తి', trainer_certs_issued: 'జారీ చేసిన ధృవపత్రాలు',
  learner_namaste: 'నమస్తే', learner_dashboard_subtitle: 'మీ RBI కెహతా హై ఆర్థిక అక్షరాస్యత శిక్షణను కొనసాగించండి',
  learner_courses_enrolled: 'నమోదైన కోర్సులు', learner_modules_completed: 'పూర్తయిన మాడ్యూల్‌లు',
  learner_avg_quiz_score: 'సగటు క్విజ్ స్కోర్', learner_certificates_earned: 'సంపాదించిన ధృవపత్రాలు',
  my_courses_title: 'నా కోర్సులు', my_courses_subtitle: 'మీరు ఆపిన చోట నుండి కొనసాగించండి, లేదా క్రింద కొత్త కోర్సులో చేరండి',
  browse_courses: 'కోర్సులను బ్రౌజ్ చేయండి', continue_course: 'కోర్సును కొనసాగించండి', take_final_quiz: 'తుది క్విజ్ తీసుకోండి',
  lessons: 'పాఠాలు', topics: 'అంశాలు', course_progress: 'కోర్సు పురోగతి', mark_complete: 'పూర్తయినట్లు గుర్తించండి',
  completed: 'పూర్తయింది', previous: 'మునుపటి', next: 'తదుపరి', enroll_now: 'ఇప్పుడే నమోదు చేసుకోండి',
  take_topic_quiz: 'అంశం క్విజ్ తీసుకోండి', topic_quiz_passed: 'క్విజ్ ఉత్తీర్ణత',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'క్విజ్ లోడ్ అవుతోంది',
  quiz_fetching_questions: 'కోర్సు నుండి ప్రశ్నలను పొందడం',
  quiz_title: 'క్విజ్.',
  quiz_no_questions_course: 'ఈ కోర్సు కోసం ఇంకా క్విజ్ ప్రశ్నలు అందుబాటులో లేవు.',
  quiz_no_questions_topic: 'ఈ అంశానికి సంబంధించి ఇంకా క్విజ్ ప్రశ్నలు ఏవీ అందుబాటులో లేవు.',
  quiz_results_title: 'క్విజ్ ఫలితాలు',
  quiz_question_prefix: 'క్విజ్, ప్రశ్న.',
  of: 'యొక్క.',
  financial_literacy_quiz: 'ఆర్థిక అక్షరాస్యత క్విజ్.',
  show_hint: 'సూచనను చూపించు',
  hide_explanation: 'వివరణను దాచు.',
  submit_quiz: 'క్విజ్ సమర్పించండి.',
  select_answer_warning: 'దయచేసి కొనసాగే ముందు సమాధానాన్ని ఎంచుకోండి',
  you_answered: 'మీరు సమాధానం ఇచ్చారు',
  out_of: 'బయటకు',
  correctly: 'సరిగ్గా',
  quiz_congrats: 'అభినందనలు! మీరు పాసయ్యారు!',
  quiz_cert_generated_note: 'మీ ప్రమాణపత్రం రూపొందించబడింది. సర్టిఫికెట్ల ప్యానెల్ నుండి దీన్ని డౌన్‌లోడ్ చేయండి.',
  quiz_need_80: 'సర్టిఫికేట్ సంపాదించడానికి మీకు కనీసం 80 శాతం అవసరం.',
  quiz_review_retry: 'మెటీరియల్‌ని రివ్యూ చేసి, మీ సర్టిఫికెట్‌ని సంపాదించడానికి మళ్లీ ప్రయత్నించండి.',
  correct_answer_label: 'సరైనది',
  retry: 'మళ్లీ ప్రయత్నించండి.',
  done: 'పూర్తయింది.',
  my_certificates: 'నా సర్టిఫికెట్లు.',
  pending_certificates: 'పెండింగ్‌లో ఉన్న సర్టిఫికెట్లు',
  download_pdf: 'PDFని డౌన్‌లోడ్ చేయండి',
  generating: 'ఉత్పత్తి చేస్తోంది',
  no_certificates_yet: 'ఇంకా సర్టిఫికెట్లు లేవు.',
  score_80_hint: 'క్విజ్‌లో ఒకటి సంపాదించడానికి కనీసం 80 శాతం స్కోర్ చేయండి.',
  not_attempted: 'ప్రయత్నించలేదు',
  need_label: 'అవసరం',
  ready_label: 'సిద్ధంగా ఉంది!',
  score_label: 'స్కోర్',
  certificate_for: 'కోసం సర్టిఫికేట్.',
  downloaded_suffix: 'downloaded!',
  cert_download_failed: 'ప్రమాణపత్రాన్ని రూపొందించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
  course_fallback: 'కోర్సు.',
  no_courses_enrolled: 'ఇంకా ఏ కోర్సులు నమోదు చేసుకోలేదు',
  no_courses_enrolled_desc: 'నమోదు చేసుకున్న కోర్సులు ఇక్కడ కనిపిస్తాయి కాబట్టి మీరు నేర్చుకోవడం కొనసాగించవచ్చు',
  continue_learning: 'నేర్చుకోవడం కొనసాగించండి',
  modules_label: 'మాడ్యూల్స్.',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'నమోదు చేసుకున్నారు',
  enrollment_failed: 'నమోదు విఫలమైంది',
  view_course: 'కోర్సును వీక్షించండి',
  kpi_need_80_certify: 'ధృవీకరించడానికి కనీసం 80 శాతం',
  kpi_no_attempts_yet: 'ఇంకా ప్రయత్నాలు లేవు.',
  courses_pending_suffix: 'కోర్సులు పెండింగ్‌లో ఉన్నాయి',
  module_marked_complete: 'మాడ్యూల్ పూర్తయినట్లు గుర్తు పెట్టబడింది!',
  failed_update_progress: 'పురోగతిని నవీకరించడంలో విఫలమైంది',
  no_downloadable_content: 'ఈ మాడ్యూల్ కోసం డౌన్‌లోడ్ చేయగల కంటెంట్ ఏదీ అందుబాటులో లేదు',
  download_started: 'డౌన్‌లోడ్ ప్రారంభమైంది!',
  download_failed_retry: 'డౌన్‌లోడ్ విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
  enrolled_loading_course: 'నమోదు చేసుకున్నారు! కోర్సు లోడ్ అవుతోంది.',
  topic_quiz_passed_toast: 'టాపిక్ క్విజ్ ఉత్తీర్ణత సాధించింది!',
  score_saved_prefix: 'స్కోర్ సేవ్ చేయబడింది.',
  need_80_to_pass: 'ఉత్తీర్ణత సాధించాలంటే కనీసం 80 శాతం ఉండాలి.',
  failed_save_quiz_results: 'క్విజ్ ఫలితాలను సేవ్ చేయడంలో విఫలమైంది',
  score_saved_cert_generated: 'స్కోర్ సేవ్ చేయబడింది! కోసం సర్టిఫికేట్ రూపొందించబడింది.',
  need_80_for_cert: 'సర్టిఫికేట్ కోసం కనీసం 80 శాతం అవసరం.',
  no_course_selected: 'ఏ కోర్సు ఎంచుకోబడలేదు. దయచేసి వెనుకకు వెళ్లి, కోర్సును ఎంచుకోండి.',
  back_to_dashboard: 'తిరిగి డాష్‌బోర్డ్‌కి.',
  need_enroll_message: 'మీరు ఈ కోర్సు యొక్క వీడియోలను చూడటానికి లేదా దాని క్విజ్ తీసుకోవడానికి ముందు మీరు ఇందులో నమోదు చేసుకోవాలి.',
  lessons_completed_suffix: 'పాఠాలు పూర్తయ్యాయి',
  no_lessons_uploaded: 'ఇంకా పాఠాలు అప్‌లోడ్ చేయబడలేదు',
  min_suffix: 'నిమి',
  no_media_uploaded: 'ఈ పాఠం కోసం మీడియా అప్‌లోడ్ చేయబడలేదు.',
  admin_trainer_upload_hint: 'అడ్మిన్/శిక్షకుడు కోర్సు నిర్వహణ ప్యానెల్ నుండి వీడియో/PDFని అప్‌లోడ్ చేయవచ్చు.',
  select_module_to_start: 'నేర్చుకోవడం ప్రారంభించడానికి మాడ్యూల్‌ను ఎంచుకోండి',
  video_label: 'వీడియో',
  pdf_label: 'PDF.',
  browser_no_video_support: 'మీ బ్రౌజర్ వీడియో ట్యాగ్‌కు మద్దతు ఇవ్వదు.',
  profile_email: 'ఇమెయిల్',
  profile_full_name: 'పూర్తి పేరు',
  profile_phone: 'ఫోన్',
  profile_village: 'గ్రామం',
  profile_district: 'జిల్లా',
  profile_age: 'వయస్సు',
  profile_gender: 'లింగం',
  profile_city: 'నగరం',
  profile_language: 'భాష',
  profile_updated_toast: 'ప్రొఫైల్ నవీకరించబడింది',
  profile_update_failed: 'ప్రొఫైల్‌ను నవీకరించడంలో విఫలమైంది',
  welcome_back_signing_in_as: 'తిరిగి స్వాగతం! మిమ్మల్ని ఇలా సైన్ ఇన్ చేస్తున్నాను',
  account_created_signing_in: 'ఖాతా సృష్టించబడింది! మిమ్మల్ని సైన్ ఇన్ చేస్తున్నాను',
  authentication_failed: 'ప్రమాణీకరణ విఫలమైంది.',
  invalid_credentials_use_demo: 'Invalid email or password. Use the demo accounts below.',
  email_already_registered: 'ఈ ఇమెయిల్ ఇప్పటికే నమోదు చేయబడింది. దయచేసి బదులుగా సైన్ ఇన్ చేయండి.',
  topic_otp_fraud: 'OTP మోసం',
  topic_upi_safety: 'UPI భద్రత',
  topic_loan_fraud: 'రుణ మోసం',
  topic_cyber_safety: 'సైబర్ భద్రత.',
  topic_kyc_basics: 'KYC బేసిక్స్',
  topic_digital_banking: 'డిజిటల్ బ్యాంకింగ్',
  placeholder_min_8_chars: 'కనిష్టంగా 8 అక్షరాలు',
  placeholder_your_password: 'మీ పాస్‌వర్డ్.',
  password_min_length_error: 'పాస్‌వర్డ్ తప్పనిసరిగా కనీసం 8 అక్షరాలు ఉండాలి',
  password_required_error: 'పాస్‌వర్డ్ అవసరం',
  placeholder_age_example: 'ఉదా 28',
  placeholder_your_city: 'మీ నగరం',
  placeholder_mobile_number: '10-అంకెల మొబైల్ నంబర్',
  select_placeholder: 'ఎంచుకోండి',
  gender_male: 'పురుషుడు',
  gender_female: 'స్త్రీ.',
  gender_other: 'ఇతర',
  quiz_results_save_error: 'మీ క్విజ్ ఫలితాలను సేవ్ చేయడంలో ఏదో తప్పు జరిగింది.',
  signed_out_success: 'విజయవంతంగా సైన్ అవుట్ చేయబడింది',
  sign_out_failed: 'సైన్ అవుట్ విఫలమైంది',
  expand_sidebar: 'సైడ్‌బార్‌ని విస్తరించండి',
  collapse_sidebar: 'సైడ్‌బార్‌ను కుదించు.',
  switch_to_light_mode: 'లైట్ మోడ్‌కి మారండి.',
  switch_to_dark_mode: 'డార్క్ మోడ్‌కి మారండి.',


  // Signup form additions
  placeholder_your_full_name: 'మీ పూర్తి పేరు',
  mobile_number_label: 'మొబైల్ నంబర్.',
  placeholder_email_example: 'you@example.com',
  copy_password_tooltip: 'పాస్వర్డ్ను కాపీ చేయండి',


  // Admin dashboard — our web properties
  our_properties_title: 'మా వెబ్ లక్షణాలు',

  property_main_site_label: 'ఫీడ్ ఫౌండేషన్',
  property_main_site_full_title: 'సమానత్వం, సమానత్వం మరియు గౌరవం కోసం పునాది',

  property_flc_site_label: 'CMS-FEED FLC',
  property_flc_site_full_title: 'CMS-FEED ఆర్థిక అక్షరాస్యత కేంద్రం',

  login_email_or_phone: 'ఇమెయిల్ లేదా మొబైల్ నంబర్',
  placeholder_email_or_phone: 'you@example.com లేదా 10-అంకెల సంఖ్య',
  login_email_required_error: 'ఇమెయిల్ అవసరం',
  login_identifier_required_error: 'ఇమెయిల్ లేదా మొబైల్ నంబర్ అవసరం',

  phone_invalid_error: 'చెల్లుబాటు అయ్యే 10-అంకెల మొబైల్ నంబర్‌ను నమోదు చేయండి',
  phone_required_error: 'మొబైల్ నంబర్ అవసరం',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'నమోదైంది',
  login_forgot_password: 'పాస్‌వర్డ్ మర్చిపోయారా?',
  fp_title: 'పాస్‌వర్డ్ మర్చిపోయారు',
  fp_subtitle: 'మీ ఇమెయిల్ లేదా మొబైల్ నంబర్ నమోదు చేయండి, మేము మీకు 6 అంకెల OTP ఇమెయిల్ చేస్తాము.',
  fp_identifier_placeholder: 'ఇమెయిల్ లేదా మొబైల్ నంబర్',
  fp_send_otp: 'OTP పంపండి',
  fp_otp_sent: 'ఖాతా ఉంటే, దాని ఇమెయిల్‌కు OTP పంపబడింది. దానిని మీ కొత్త పాస్‌వర్డ్‌తో పాటు కింద నమోదు చేయండి. OTP 10 నిమిషాలు చెల్లుబాటు అవుతుంది.',
  fp_otp_placeholder: '6 అంకెల OTP',
  fp_new_password: 'కొత్త పాస్‌వర్డ్ (కనీసం 8 అక్షరాలు)',
  fp_confirm_password: 'కొత్త పాస్‌వర్డ్‌ను నిర్ధారించండి',
  fp_reset_password: 'పాస్‌వర్డ్ రీసెట్ చేయండి',
  fp_resend_otp: 'OTP మళ్లీ పంపండి',
  fp_done: 'మీ పాస్‌వర్డ్ అప్‌డేట్ చేయబడింది.',
  fp_pw_min: 'పాస్‌వర్డ్ కనీసం 8 అక్షరాలు ఉండాలి.',
  fp_pw_mismatch: 'పాస్‌వర్డ్‌లు సరిపోలడం లేదు.',
  fp_back_to_signin: 'సైన్ ఇన్‌కు తిరిగి వెళ్లండి',
  not_found_title: 'పేజీ కనుగొనబడలేదు',
  not_found_desc: 'మీరు వెతుకుతున్న పేజీ లేదు. మిమ్మల్ని వెనక్కి తీసుకెళ్దాం!',
  go_back: 'వెనక్కి వెళ్లండి',
  back_to_home: 'హోమ్‌కు తిరిగి వెళ్లండి',
  trainer_my_courses: 'నా కోర్సులు',
  trainer_courses_by_you: 'మీరు సృష్టించిన కోర్సు(లు)',
  trainer_add_course: 'కోర్సు జోడించండి',
  trainer_modules_count: 'మాడ్యూల్(లు)',
  trainer_manage_modules: 'వీడియోలు/మాడ్యూళ్లను నిర్వహించండి',
  trainer_edit_course: 'కోర్సును సవరించండి',
  trainer_delete_course: 'కోర్సును తొలగించండి',
  trainer_completion_overview: 'కోర్సు పూర్తి అవలోకనం',
  login_initiative: 'FEED — ఫౌండేషన్ ఫర్ ఈక్వాలిటీ, ఈక్విటీ అండ్ డిగ్నిటీ & CMS ఫౌండేషన్ సంయుక్త కార్యక్రమం',
};

const ta: Dict = {
  save: 'சேமி', save_changes: 'மாற்றங்களைச் சேமி', cancel: 'ரத்துசெய்', delete: 'நீக்கு', edit: 'திருத்து',
  add: 'சேர்', search: 'தேடு', close: 'மூடு', loading: 'ஏற்றுகிறது…', saving: 'சேமிக்கிறது…',
  refresh: 'புதுப்பி', back: 'பின்', continue: 'தொடர்', enroll: 'பதிவு செய்', enrolling: 'பதிவு செய்கிறது…',
  download: 'பதிவிறக்கு', sign_out: 'வெளியேறு', signing_out: 'வெளியேறுகிறது…', my_profile: 'என் சுயவிவரம்',
  settings: 'அமைப்புகள்', nav_dashboard: 'டாஷ்போர்டு', nav_reports: 'அறிக்கைகள்', nav_courses: 'பாடநெறிகள்',
  nav_course_management: 'பாடநெறி மேலாண்மை', nav_media: 'வீடியோக்கள் & மீடியா', nav_assessments: 'மதிப்பீடுகள்',
  nav_members: 'உறுப்பினர்கள்', nav_trainers: 'பயிற்றுநர்கள்', nav_certificates: 'சான்றிதழ்கள்',
  nav_notifications: 'அறிவிப்புகள்', nav_settings: 'அமைப்புகள்', nav_learner_progress: 'கற்பவர் முன்னேற்றம்',
  nav_my_dashboard: 'என் டாஷ்போர்டு', nav_my_courses: 'என் பாடநெறிகள்', nav_help: 'உதவி',
  group_overview: 'மேலோட்டம்', group_content: 'உள்ளடக்கம்', group_people: 'நபர்கள்', group_system: 'அமைப்பு',
  group_my_work: 'என் பணி', group_learning: 'கற்றல்', group_support: 'ஆதரவு',
  role_admin: 'நிர்வாகி — FEED HQ', role_trainer: 'கள பயிற்றுநர்', role_learner: 'கற்பவர்',
  login_welcome_back: 'மீண்டும் வரவேற்கிறோம்', login_create_account: 'கணக்கை உருவாக்கு',
  login_signin_subtitle: 'உங்கள் நிதி கல்வியறிவு பயணத்தைத் தொடர உள்நுழையவும்',
  login_signup_subtitle: 'RBI கெஹ்தா ஹை பயிற்சி திட்டத்தில் இணையவும்', login_i_am_a: 'நான் ஒரு',
  login_role_learner: 'கற்பவர்', login_role_trainer: 'கள பயிற்றுநர்', login_role_admin: 'நிர்வாகி',
  login_full_name: 'முழு பெயர்', login_email: 'மின்னஞ்சல் முகவரி', login_password: 'கடவுச்சொல்',
  login_sign_in: 'உள்நுழை', login_sign_up: 'கணக்கை உருவாக்கு', login_signing_in: 'உள்நுழைகிறது…',
  login_creating_account: 'கணக்கு உருவாகிறது…', login_no_account: 'கணக்கு இல்லையா? ', login_have_account: 'ஏற்கனவே கணக்கு உள்ளதா? ',
  login_toggle_signup: 'பதிவு செய்', login_toggle_signin: 'உள்நுழை', login_demo_accounts: '🔒 டெமோ கணக்குகள் — நிரப்ப கிளிக் செய்யவும்',
  login_hero_tagline: 'RBI கெஹ்தா ஹை பிரச்சாரம்',
  login_hero_desc: 'கற்பவர்கள், சமூக உறுப்பினர்கள் மற்றும் கள பணியாளர்களுக்கான நிதி கல்வியறிவு பயிற்சி — OTP மோசடி, UPI பாதுகாப்பு, கடன் மோசடி மற்றும் சைபர் பாதுகாப்பு.',
  admin_dashboard_title: 'பயிற்சி தாக்க டாஷ்போர்டு', admin_dashboard_subtitle: 'RBI கெஹ்தா ஹை — FEED & CMS அறக்கட்டளை',
  export_report: 'அறிக்கையை ஏற்றுமதி செய்', filter: 'வடிகட்டி', this_week: 'இந்த வாரம்', this_month: 'இந்த மாதம்',
  last_3_months: 'கடந்த 3 மாதங்கள்', all_time: 'எல்லா காலமும்', kpi_members_trained: 'பயிற்சி பெற்ற உறுப்பினர்கள்',
  kpi_members_trained_sub: 'மொத்த பதிவு செய்யப்பட்ட கற்பவர்கள்', kpi_certificates_issued: 'வழங்கப்பட்ட சான்றிதழ்கள்',
  kpi_all_time: 'எல்லா காலமும்', kpi_members_pending: 'நிலுவையில் உள்ள உறுப்பினர்கள்', kpi_members_pending_sub: 'இன்னும் பதிவு செய்யவில்லை',
  kpi_pass_rate: 'ஒட்டுமொத்த தேர்ச்சி விகிதம்', kpi_districts_covered: 'உள்ளடக்கிய மாவட்டங்கள்', kpi_districts_covered_sub: 'அனைத்து கற்பவர்களிலும்',
  kpi_active_courses: 'செயலில் உள்ள பாடநெறிகள்', kpi_active_courses_sub: 'வெளியிடப்பட்டு கிடைக்கிறது',
  trainer_dashboard_title: 'பயிற்றுநர் டாஷ்போர்டு', trainer_courses_created: 'உருவாக்கப்பட்ட பாடநெறிகள்',
  trainer_total_learners: 'மொத்த கற்பவர்கள்', trainer_avg_completion: 'சராசரி நிறைவு', trainer_certs_issued: 'வழங்கப்பட்ட சான்றிதழ்கள்',
  learner_namaste: 'வணக்கம்', learner_dashboard_subtitle: 'உங்கள் RBI கெஹ்தா ஹை நிதி கல்வியறிவு பயிற்சியைத் தொடரவும்',
  learner_courses_enrolled: 'பதிவு செய்யப்பட்ட பாடநெறிகள்', learner_modules_completed: 'முடிக்கப்பட்ட தொகுதிகள்',
  learner_avg_quiz_score: 'சராசரி வினாடி வினா மதிப்பெண்', learner_certificates_earned: 'பெற்ற சான்றிதழ்கள்',
  my_courses_title: 'என் பாடநெறிகள்', my_courses_subtitle: 'நீங்கள் நிறுத்திய இடத்திலிருந்து தொடரவும், அல்லது கீழே புதிய பாடநெறியில் சேரவும்',
  browse_courses: 'பாடநெறிகளை உலாவு', continue_course: 'பாடநெறியைத் தொடரவும்', take_final_quiz: 'இறுதி வினாடி வினா எடு',
  lessons: 'பாடங்கள்', topics: 'தலைப்புகள்', course_progress: 'பாடநெறி முன்னேற்றம்', mark_complete: 'முடிந்ததாகக் குறி',
  completed: 'முடிந்தது', previous: 'முந்தைய', next: 'அடுத்தது', enroll_now: 'இப்போது பதிவு செய்',
  take_topic_quiz: 'தலைப்பு வினாடி வினா எடு', topic_quiz_passed: 'வினாடி வினா தேர்ச்சி',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'வினாடி வினா ஏற்றுகிறது.',
  quiz_fetching_questions: 'பாடத்திலிருந்து கேள்விகளைப் பெறுதல்',
  quiz_title: 'வினாடி வினா',
  quiz_no_questions_course: 'இந்தப் பாடநெறிக்கான வினாடி வினா கேள்விகள் எதுவும் இதுவரை இல்லை.',
  quiz_no_questions_topic: 'இந்தத் தலைப்புக்கான வினாடி வினா கேள்விகள் எதுவும் இதுவரை கிடைக்கவில்லை.',
  quiz_results_title: 'வினாடி வினா முடிவுகள்',
  quiz_question_prefix: 'வினாடி வினா, கேள்வி.',
  of: 'இன்',
  financial_literacy_quiz: 'நிதி கல்வியறிவு வினாடிவினா',
  show_hint: 'குறிப்பைக் காட்டு',
  hide_explanation: 'விளக்கத்தை மறை.',
  submit_quiz: 'வினாடி வினாவைச் சமர்ப்பிக்கவும்',
  select_answer_warning: 'தொடர்வதற்கு முன் ஒரு பதிலைத் தேர்ந்தெடுக்கவும்.',
  you_answered: 'நீங்கள் பதிலளித்தீர்கள்',
  out_of: 'வெளியே',
  correctly: 'சரியாக',
  quiz_congrats: 'வாழ்த்துகள்! நீ தேர்ச்சி பெற்றாய்!',
  quiz_cert_generated_note: 'உங்கள் சான்றிதழ் உருவாக்கப்பட்டுள்ளது. சான்றிதழ் பேனலில் இருந்து பதிவிறக்கவும்.',
  quiz_need_80: 'சான்றிதழைப் பெற உங்களுக்கு குறைந்தபட்சம் 80 சதவீதம் தேவை',
  quiz_review_retry: 'உள்ளடக்கத்தை மதிப்பாய்வு செய்து, உங்கள் சான்றிதழைப் பெற மீண்டும் முயற்சிக்கவும்.',
  correct_answer_label: 'சரி',
  retry: 'மீண்டும் முயற்சிக்கவும்',
  done: 'முடிந்தது',
  my_certificates: 'எனது சான்றிதழ்கள்',
  pending_certificates: 'நிலுவையில் உள்ள சான்றிதழ்கள்.',
  download_pdf: 'PDF ஐப் பதிவிறக்கவும்.',
  generating: 'உருவாக்குகிறது.',
  no_certificates_yet: 'இதுவரை சான்றிதழ்கள் இல்லை.',
  score_80_hint: 'வினாடி வினாவில் குறைந்தபட்சம் 80 சதவீத மதிப்பெண்களைப் பெறுங்கள்',
  not_attempted: 'முயற்சிக்கவில்லை.',
  need_label: 'தேவை.',
  ready_label: 'தயார்!',
  score_label: 'மதிப்பெண்',
  certificate_for: 'இதற்கான சான்றிதழ்.',
  downloaded_suffix: 'பதிவிறக்கம் செய்யப்பட்டது!',
  cert_download_failed: 'சான்றிதழை உருவாக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
  course_fallback: 'பாடநெறி',
  no_courses_enrolled: 'இன்னும் படிப்புகள் எதுவும் பதிவு செய்யப்படவில்லை',
  no_courses_enrolled_desc: 'பதிவுசெய்யப்பட்ட படிப்புகள் இங்கே தோன்றும், எனவே நீங்கள் தொடர்ந்து கற்கலாம்',
  continue_learning: 'தொடர்ந்து கற்றுக்கொள்',
  modules_label: 'தொகுதிகள்.',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'பதிவு செய்துள்ளார்',
  enrollment_failed: 'பதிவு தோல்வியடைந்தது',
  view_course: 'பாடத்திட்டத்தைப் பார்க்கவும்',
  kpi_need_80_certify: 'சான்றளிக்க குறைந்தபட்சம் 80 சதவீதம்',
  kpi_no_attempts_yet: 'இன்னும் முயற்சிகள் இல்லை',
  courses_pending_suffix: 'படிப்புகள் நிலுவையில் உள்ளன.',
  module_marked_complete: 'தொகுதி முடிந்ததாகக் குறிக்கப்பட்டது!',
  failed_update_progress: 'முன்னேற்றத்தைப் புதுப்பிக்க முடியவில்லை',
  no_downloadable_content: 'இந்த தொகுதிக்கு பதிவிறக்கக்கூடிய உள்ளடக்கம் இல்லை',
  download_started: 'பதிவிறக்கம் தொடங்கியது!',
  download_failed_retry: 'பதிவிறக்கம் தோல்வியடைந்தது. மீண்டும் முயற்சிக்கவும்.',
  enrolled_loading_course: 'பதிவுசெய்யப்பட்டது! பாடத்தை ஏற்றுகிறது',
  topic_quiz_passed_toast: 'தலைப்பு வினாடி வினா தேர்ச்சி!',
  score_saved_prefix: 'ஸ்கோர் சேமிக்கப்பட்டது.',
  need_80_to_pass: 'தேர்ச்சி பெற குறைந்தபட்சம் 80 சதவீதம் தேவை.',
  failed_save_quiz_results: 'வினாடி வினா முடிவுகளைச் சேமிக்க முடியவில்லை.',
  score_saved_cert_generated: 'ஸ்கோர் சேமிக்கப்பட்டது! இதற்கான சான்றிதழ் உருவாக்கப்பட்டது',
  need_80_for_cert: 'சான்றிதழுக்கு குறைந்தபட்சம் 80 சதவீதம் தேவை.',
  no_course_selected: 'பாடம் எதுவும் தேர்ந்தெடுக்கப்படவில்லை. தயவுசெய்து திரும்பிச் சென்று பாடத்தைத் தேர்ந்தெடுக்கவும்.',
  back_to_dashboard: 'டாஷ்போர்டுக்குத் திரும்பு',
  need_enroll_message: 'இந்த பாடத்திட்டத்தின் வீடியோக்களை பார்ப்பதற்கு முன் அல்லது அதன் வினாடி வினாவை எடுப்பதற்கு முன் நீங்கள் இதில் சேர வேண்டும்.',
  lessons_completed_suffix: 'பாடங்கள் முடிந்தது',
  no_lessons_uploaded: 'பாடங்கள் எதுவும் பதிவேற்றப்படவில்லை',
  min_suffix: 'நிமிடம்',
  no_media_uploaded: 'இந்தப் பாடத்திற்கு எந்த ஊடகமும் பதிவேற்றப்படவில்லை',
  admin_trainer_upload_hint: 'நிர்வாகி/பயிற்சியாளர் பாட மேலாண்மை குழுவிலிருந்து வீடியோ/PDF ஐ பதிவேற்றலாம்',
  select_module_to_start: 'கற்றலைத் தொடங்க ஒரு தொகுதியைத் தேர்ந்தெடுக்கவும்.',
  video_label: 'வீடியோ.',
  pdf_label: 'PDF.',
  browser_no_video_support: 'உங்கள் உலாவி வீடியோ குறிச்சொல்லை ஆதரிக்கவில்லை.',
  profile_email: 'மின்னஞ்சல்.',
  profile_full_name: 'முழுப் பெயர்',
  profile_phone: 'தொலைபேசி',
  profile_village: 'கிராமம்',
  profile_district: 'மாவட்டம்',
  profile_age: 'வயது.',
  profile_gender: 'பாலினம்.',
  profile_city: 'நகரம்.',
  profile_language: 'மொழி',
  profile_updated_toast: 'சுயவிவரம் புதுப்பிக்கப்பட்டது.',
  profile_update_failed: 'சுயவிவரத்தைப் புதுப்பிக்க முடியவில்லை.',
  welcome_back_signing_in_as: 'மீண்டும் வருக! என உள்நுழைகிறேன்.',
  account_created_signing_in: 'கணக்கு உருவாக்கப்பட்டது! உங்களை உள்நுழைகிறேன்',
  authentication_failed: 'அங்கீகாரம் தோல்வியடைந்தது',
  invalid_credentials_use_demo: 'தவறான மின்னஞ்சல் அல்லது கடவுச்சொல். கீழே உள்ள டெமோ கணக்குகளைப் பயன்படுத்தவும்.',
  email_already_registered: 'இந்த மின்னஞ்சல் ஏற்கனவே பதிவு செய்யப்பட்டுள்ளது. அதற்கு பதிலாக உள்நுழையவும்.',
  topic_otp_fraud: 'OTP மோசடி',
  topic_upi_safety: 'UPI பாதுகாப்பு',
  topic_loan_fraud: 'கடன் மோசடி',
  topic_cyber_safety: 'சைபர் பாதுகாப்பு.',
  topic_kyc_basics: 'KYC அடிப்படைகள்',
  topic_digital_banking: 'டிஜிட்டல் வங்கி',
  placeholder_min_8_chars: 'குறைந்தபட்சம் 8 எழுத்துகள்.',
  placeholder_your_password: 'உங்கள் கடவுச்சொல்',
  password_min_length_error: 'கடவுச்சொல் குறைந்தது 8 எழுத்துகளாக இருக்க வேண்டும்',
  password_required_error: 'கடவுச்சொல் தேவை',
  placeholder_age_example: 'எ.கா. 28',
  placeholder_your_city: 'உங்கள் நகரம்',
  placeholder_mobile_number: '10 இலக்க மொபைல் எண்.',
  select_placeholder: 'தேர்ந்தெடு.',
  gender_male: 'ஆண்',
  gender_female: 'பெண்.',
  gender_other: 'மற்றவை.',
  quiz_results_save_error: 'உங்கள் வினாடி வினா முடிவுகளைச் சேமிப்பதில் ஏதோ தவறு ஏற்பட்டது.',
  signed_out_success: 'வெற்றிகரமாக வெளியேறியது',
  sign_out_failed: 'வெளியேற முடியவில்லை.',
  expand_sidebar: 'பக்கப்பட்டியை விரிவாக்கு',
  collapse_sidebar: 'பக்கப்பட்டியைச் சுருக்கவும்',
  switch_to_light_mode: 'ஒளி பயன்முறைக்கு மாறவும்',
  switch_to_dark_mode: 'இருண்ட பயன்முறைக்கு மாறவும்.',


  // Signup form additions
  placeholder_your_full_name: 'உங்கள் முழு பெயர்.',
  mobile_number_label: 'மொபைல் எண்.',
  placeholder_email_example: 'you@example.com.',
  copy_password_tooltip: 'கடவுச்சொல்லை நகலெடுக்கவும்',


  // Admin dashboard — our web properties
  our_properties_title: 'எங்கள் இணைய பண்புகள்',

  property_main_site_label: 'FEED அறக்கட்டளை',
  property_main_site_full_title: 'சமத்துவம், சமத்துவம் மற்றும் கண்ணியத்திற்கான அடித்தளம்',

  property_flc_site_label: 'CMS-FEED FLC.',
  property_flc_site_full_title: 'CMS-FEED நிதி கல்வியறிவு மையம்',

  login_email_or_phone: 'மின்னஞ்சல் அல்லது மொபைல் எண்',
  placeholder_email_or_phone: 'you@example.com அல்லது 10 இலக்க எண்',
  login_email_required_error: 'மின்னஞ்சல் தேவை',
  login_identifier_required_error: 'மின்னஞ்சல் அல்லது மொபைல் எண் தேவை',

  phone_invalid_error: 'சரியான 10 இலக்க மொபைல் எண்ணை உள்ளிடவும்.',
  phone_required_error: 'மொபைல் எண் தேவை',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'பதிவு செய்யப்பட்டது',
  login_forgot_password: 'கடவுச்சொல்லை மறந்துவிட்டீர்களா?',
  fp_title: 'கடவுச்சொல் மறந்துவிட்டது',
  fp_subtitle: 'உங்கள் மின்னஞ்சல் அல்லது மொபைல் எண்ணை உள்ளிடவும், 6 இலக்க OTP-ஐ மின்னஞ்சலில் அனுப்புவோம்.',
  fp_identifier_placeholder: 'மின்னஞ்சல் அல்லது மொபைல் எண்',
  fp_send_otp: 'OTP அனுப்பு',
  fp_otp_sent: 'கணக்கு இருந்தால், அதன் மின்னஞ்சலுக்கு OTP அனுப்பப்பட்டுள்ளது. அதை புதிய கடவுச்சொல்லுடன் கீழே உள்ளிடவும். OTP 10 நிமிடங்கள் செல்லுபடியாகும்.',
  fp_otp_placeholder: '6 இலக்க OTP',
  fp_new_password: 'புதிய கடவுச்சொல் (குறைந்தது 8 எழுத்துகள்)',
  fp_confirm_password: 'புதிய கடவுச்சொல்லை உறுதிப்படுத்தவும்',
  fp_reset_password: 'கடவுச்சொல்லை மீட்டமை',
  fp_resend_otp: 'OTP மீண்டும் அனுப்பு',
  fp_done: 'உங்கள் கடவுச்சொல் புதுப்பிக்கப்பட்டது.',
  fp_pw_min: 'கடவுச்சொல் குறைந்தது 8 எழுத்துகள் இருக்க வேண்டும்.',
  fp_pw_mismatch: 'கடவுச்சொற்கள் பொருந்தவில்லை.',
  fp_back_to_signin: 'உள்நுழைவுக்குத் திரும்பு',
  not_found_title: 'பக்கம் கிடைக்கவில்லை',
  not_found_desc: 'நீங்கள் தேடும் பக்கம் இல்லை. உங்களைத் திரும்ப அழைத்துச் செல்கிறோம்!',
  go_back: 'பின் செல்',
  back_to_home: 'முகப்புக்குத் திரும்பு',
  trainer_my_courses: 'என் படிப்புகள்',
  trainer_courses_by_you: 'நீங்கள் உருவாக்கிய படிப்பு(கள்)',
  trainer_add_course: 'படிப்பைச் சேர்',
  trainer_modules_count: 'தொகுதி(கள்)',
  trainer_manage_modules: 'வீடியோக்கள்/தொகுதிகளை நிர்வகி',
  trainer_edit_course: 'படிப்பைத் திருத்து',
  trainer_delete_course: 'படிப்பை நீக்கு',
  trainer_completion_overview: 'படிப்பு நிறைவு கண்ணோட்டம்',
  login_initiative: 'FEED — சமத்துவம், நேர்மை மற்றும் கண்ணியத்திற்கான அறக்கட்டளை & CMS அறக்கட்டளையின் கூட்டு முயற்சி',
};

const kn: Dict = {
  save: 'ಉಳಿಸಿ', save_changes: 'ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ', cancel: 'ರದ್ದುಮಾಡಿ', delete: 'ಅಳಿಸಿ', edit: 'ಸಂಪಾದಿಸಿ',
  add: 'ಸೇರಿಸಿ', search: 'ಹುಡುಕಿ', close: 'ಮುಚ್ಚಿ', loading: 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…', saving: 'ಉಳಿಸುತ್ತಿದೆ…',
  refresh: 'ರಿಫ್ರೆಶ್ ಮಾಡಿ', back: 'ಹಿಂದೆ', continue: 'ಮುಂದುವರಿಸಿ', enroll: 'ದಾಖಲಾಗಿ', enrolling: 'ದಾಖಲಾಗುತ್ತಿದೆ…',
  download: 'ಡೌನ್‌ಲೋಡ್', sign_out: 'ಸೈನ್ ಔಟ್', signing_out: 'ಸೈನ್ ಔಟ್ ಆಗುತ್ತಿದೆ…', my_profile: 'ನನ್ನ ಪ್ರೊಫೈಲ್',
  settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು', nav_dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್', nav_reports: 'ವರದಿಗಳು', nav_courses: 'ಕೋರ್ಸ್‌ಗಳು',
  nav_course_management: 'ಕೋರ್ಸ್ ನಿರ್ವಹಣೆ', nav_media: 'ವೀಡಿಯೊಗಳು & ಮೀಡಿಯಾ', nav_assessments: 'ಮೌಲ್ಯಮಾಪನಗಳು',
  nav_members: 'ಸದಸ್ಯರು', nav_trainers: 'ತರಬೇತುದಾರರು', nav_certificates: 'ಪ್ರಮಾಣಪತ್ರಗಳು', nav_notifications: 'ಅಧಿಸೂಚನೆಗಳು',
  nav_settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು', nav_learner_progress: 'ಕಲಿಯುವವರ ಪ್ರಗತಿ', nav_my_dashboard: 'ನನ್ನ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
  nav_my_courses: 'ನನ್ನ ಕೋರ್ಸ್‌ಗಳು', nav_help: 'ಸಹಾಯ', group_overview: 'ಅವಲೋಕನ', group_content: 'ವಿಷಯ',
  group_people: 'ಜನರು', group_system: 'ಸಿಸ್ಟಮ್', group_my_work: 'ನನ್ನ ಕೆಲಸ', group_learning: 'ಕಲಿಕೆ', group_support: 'ಬೆಂಬಲ',
  role_admin: 'ಅಡ್ಮಿನ್ — FEED HQ', role_trainer: 'ಫೀಲ್ಡ್ ತರಬೇತುದಾರ', role_learner: 'ಕಲಿಯುವವರು',
  login_welcome_back: 'ಮತ್ತೆ ಸ್ವಾಗತ', login_create_account: 'ಖಾತೆ ರಚಿಸಿ',
  login_signin_subtitle: 'ನಿಮ್ಮ ಆರ್ಥಿಕ ಸಾಕ್ಷರತಾ ಪ್ರಯಾಣವನ್ನು ಮುಂದುವರಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ',
  login_signup_subtitle: 'RBI ಕೆಹ್ತಾ ಹೈ ತರಬೇತಿ ಕಾರ್ಯಕ್ರಮಕ್ಕೆ ಸೇರಿ', login_i_am_a: 'ನಾನು ಒಬ್ಬ',
  login_role_learner: 'ಕಲಿಯುವವರು', login_role_trainer: 'ಫೀಲ್ಡ್ ತರಬೇತುದಾರ', login_role_admin: 'ಅಡ್ಮಿನ್',
  login_full_name: 'ಪೂರ್ಣ ಹೆಸರು', login_email: 'ಇಮೇಲ್ ವಿಳಾಸ', login_password: 'ಪಾಸ್‌ವರ್ಡ್',
  login_sign_in: 'ಸೈನ್ ಇನ್', login_sign_up: 'ಖಾತೆ ರಚಿಸಿ', login_signing_in: 'ಸೈನ್ ಇನ್ ಆಗುತ್ತಿದೆ…',
  login_creating_account: 'ಖಾತೆ ರಚಿಸಲಾಗುತ್ತಿದೆ…', login_no_account: 'ಖಾತೆ ಇಲ್ಲವೇ? ', login_have_account: 'ಈಗಾಗಲೇ ಖಾತೆ ಇದೆಯೇ? ',
  login_toggle_signup: 'ಸೈನ್ ಅಪ್', login_toggle_signin: 'ಸೈನ್ ಇನ್', login_demo_accounts: '🔒 ಡೆಮೊ ಖಾತೆಗಳು — ಭರ್ತಿ ಮಾಡಲು ಕ್ಲಿಕ್ ಮಾಡಿ',
  login_hero_tagline: 'RBI ಕೆಹ್ತಾ ಹೈ ಅಭಿಯಾನ',
  login_hero_desc: 'ಕಲಿಯುವವರು, ಸಮುದಾಯ ಸದಸ್ಯರು ಮತ್ತು ಫೀಲ್ಡ್ ಸಿಬ್ಬಂದಿಗಾಗಿ ಆರ್ಥಿಕ ಸಾಕ್ಷರತಾ ತರಬೇತಿ — OTP ವಂಚನೆ, UPI ಸುರಕ್ಷತೆ, ಸಾಲ ವಂಚನೆ ಮತ್ತು ಸೈಬರ್ ಸುರಕ್ಷತೆ.',
  admin_dashboard_title: 'ತರಬೇತಿ ಪರಿಣಾಮ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್', admin_dashboard_subtitle: 'RBI ಕೆಹ್ತಾ ಹೈ — FEED & CMS ಫೌಂಡೇಶನ್',
  export_report: 'ವರದಿ ರಫ್ತು ಮಾಡಿ', filter: 'ಫಿಲ್ಟರ್', this_week: 'ಈ ವಾರ', this_month: 'ಈ ತಿಂಗಳು',
  last_3_months: 'ಕಳೆದ 3 ತಿಂಗಳು', all_time: 'ಎಲ್ಲಾ ಸಮಯ', kpi_members_trained: 'ತರಬೇತಿ ಪಡೆದ ಸದಸ್ಯರು',
  kpi_members_trained_sub: 'ಒಟ್ಟು ನೋಂದಾಯಿತ ಕಲಿಯುವವರು', kpi_certificates_issued: 'ನೀಡಲಾದ ಪ್ರಮಾಣಪತ್ರಗಳು',
  kpi_all_time: 'ಎಲ್ಲಾ ಸಮಯ', kpi_members_pending: 'ಬಾಕಿ ಇರುವ ಸದಸ್ಯರು', kpi_members_pending_sub: 'ಇನ್ನೂ ದಾಖಲಾಗಿಲ್ಲ',
  kpi_pass_rate: 'ಒಟ್ಟಾರೆ ಉತ್ತೀರ್ಣ ದರ', kpi_districts_covered: 'ಒಳಗೊಂಡ ಜಿಲ್ಲೆಗಳು', kpi_districts_covered_sub: 'ಎಲ್ಲಾ ಕಲಿಯುವವರಲ್ಲಿ',
  kpi_active_courses: 'ಸಕ್ರಿಯ ಕೋರ್ಸ್‌ಗಳು', kpi_active_courses_sub: 'ಪ್ರಕಟಿಸಲಾಗಿದೆ ಮತ್ತು ಲಭ್ಯವಿದೆ',
  trainer_dashboard_title: 'ತರಬೇತುದಾರ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್', trainer_courses_created: 'ರಚಿಸಲಾದ ಕೋರ್ಸ್‌ಗಳು',
  trainer_total_learners: 'ಒಟ್ಟು ಕಲಿಯುವವರು', trainer_avg_completion: 'ಸರಾಸರಿ ಪೂರ್ಣಗೊಳಿಸುವಿಕೆ', trainer_certs_issued: 'ನೀಡಲಾದ ಪ್ರಮಾಣಪತ್ರಗಳು',
  learner_namaste: 'ನಮಸ್ತೆ', learner_dashboard_subtitle: 'ನಿಮ್ಮ RBI ಕೆಹ್ತಾ ಹೈ ಆರ್ಥಿಕ ಸಾಕ್ಷರತಾ ತರಬೇತಿಯನ್ನು ಮುಂದುವರಿಸಿ',
  learner_courses_enrolled: 'ದಾಖಲಾದ ಕೋರ್ಸ್‌ಗಳು', learner_modules_completed: 'ಪೂರ್ಣಗೊಂಡ ಮಾಡ್ಯೂಲ್‌ಗಳು',
  learner_avg_quiz_score: 'ಸರಾಸರಿ ಕ್ವಿಜ್ ಸ್ಕೋರ್', learner_certificates_earned: 'ಗಳಿಸಿದ ಪ್ರಮಾಣಪತ್ರಗಳು',
  my_courses_title: 'ನನ್ನ ಕೋರ್ಸ್‌ಗಳು', my_courses_subtitle: 'ನೀವು ಬಿಟ್ಟಲ್ಲಿಂದ ಮುಂದುವರಿಸಿ, ಅಥವಾ ಕೆಳಗೆ ಹೊಸ ಕೋರ್ಸ್‌ಗೆ ದಾಖಲಾಗಿ',
  browse_courses: 'ಕೋರ್ಸ್‌ಗಳನ್ನು ಬ್ರೌಸ್ ಮಾಡಿ', continue_course: 'ಕೋರ್ಸ್ ಮುಂದುವರಿಸಿ', take_final_quiz: 'ಅಂತಿಮ ಕ್ವಿಜ್ ತೆಗೆದುಕೊಳ್ಳಿ',
  lessons: 'ಪಾಠಗಳು', topics: 'ವಿಷಯಗಳು', course_progress: 'ಕೋರ್ಸ್ ಪ್ರಗತಿ', mark_complete: 'ಪೂರ್ಣಗೊಂಡಂತೆ ಗುರುತಿಸಿ',
  completed: 'ಪೂರ್ಣಗೊಂಡಿದೆ', previous: 'ಹಿಂದಿನ', next: 'ಮುಂದಿನ', enroll_now: 'ಈಗಲೇ ದಾಖಲಾಗಿ',
  take_topic_quiz: 'ವಿಷಯ ಕ್ವಿಜ್ ತೆಗೆದುಕೊಳ್ಳಿ', topic_quiz_passed: 'ಕ್ವಿಜ್ ಉತ್ತೀರ್ಣ',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'ರಸಪ್ರಶ್ನೆ ಲೋಡ್ ಆಗುತ್ತಿದೆ',
  quiz_fetching_questions: 'ಕೋರ್ಸ್‌ನಿಂದ ಪ್ರಶ್ನೆಗಳನ್ನು ಪಡೆಯಲಾಗುತ್ತಿದೆ',
  quiz_title: 'ರಸಪ್ರಶ್ನೆ.',
  quiz_no_questions_course: 'ಈ ಕೋರ್ಸ್‌ಗೆ ಇನ್ನೂ ಯಾವುದೇ ರಸಪ್ರಶ್ನೆ ಪ್ರಶ್ನೆಗಳು ಲಭ್ಯವಿಲ್ಲ.',
  quiz_no_questions_topic: 'ಈ ವಿಷಯಕ್ಕೆ ಇನ್ನೂ ಯಾವುದೇ ರಸಪ್ರಶ್ನೆ ಪ್ರಶ್ನೆಗಳು ಲಭ್ಯವಿಲ್ಲ.',
  quiz_results_title: 'ರಸಪ್ರಶ್ನೆ ಫಲಿತಾಂಶಗಳು',
  quiz_question_prefix: 'ರಸಪ್ರಶ್ನೆ, ಪ್ರಶ್ನೆ.',
  of: 'of',
  financial_literacy_quiz: 'ಆರ್ಥಿಕ ಸಾಕ್ಷರತೆ ರಸಪ್ರಶ್ನೆ',
  show_hint: 'ಸುಳಿವು ತೋರಿಸಿ.',
  hide_explanation: 'ವಿವರಣೆಯನ್ನು ಮರೆಮಾಡಿ',
  submit_quiz: 'ರಸಪ್ರಶ್ನೆ ಸಲ್ಲಿಸಿ',
  select_answer_warning: 'ಮುಂದುವರಿಯುವ ಮೊದಲು ದಯವಿಟ್ಟು ಉತ್ತರವನ್ನು ಆಯ್ಕೆಮಾಡಿ.',
  you_answered: 'ನೀವು ಉತ್ತರಿಸಿದ್ದೀರಿ',
  out_of: 'ಹೊರಗೆ',
  correctly: 'ಸರಿಯಾಗಿ',
  quiz_congrats: 'Congratulations! You passed!',
  quiz_cert_generated_note: 'ನಿಮ್ಮ ಪ್ರಮಾಣಪತ್ರವನ್ನು ರಚಿಸಲಾಗಿದೆ. ಪ್ರಮಾಣಪತ್ರಗಳ ಫಲಕದಿಂದ ಅದನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ.',
  quiz_need_80: 'ಪ್ರಮಾಣಪತ್ರವನ್ನು ಗಳಿಸಲು ನಿಮಗೆ ಕನಿಷ್ಠ 80 ಪ್ರತಿಶತ ಅಗತ್ಯವಿದೆ',
  quiz_review_retry: 'ವಿಷಯವನ್ನು ಪರಿಶೀಲಿಸಿ ಮತ್ತು ನಿಮ್ಮ ಪ್ರಮಾಣಪತ್ರವನ್ನು ಗಳಿಸಲು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
  correct_answer_label: 'ಸರಿ.',
  retry: 'ಮರುಪ್ರಯತ್ನಿಸಿ',
  done: 'ಮುಗಿದಿದೆ.',
  my_certificates: 'ನನ್ನ ಪ್ರಮಾಣಪತ್ರಗಳು',
  pending_certificates: 'ಬಾಕಿ ಇರುವ ಪ್ರಮಾಣಪತ್ರಗಳು',
  download_pdf: 'PDF ಅನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
  generating: 'ಉತ್ಪಾದಿಸುತ್ತಿದೆ.',
  no_certificates_yet: 'ಇನ್ನೂ ಯಾವುದೇ ಪ್ರಮಾಣಪತ್ರಗಳಿಲ್ಲ',
  score_80_hint: 'ಒಂದನ್ನು ಗಳಿಸಲು ರಸಪ್ರಶ್ನೆಯಲ್ಲಿ ಕನಿಷ್ಠ 80 ಪ್ರತಿಶತ ಸ್ಕೋರ್ ಮಾಡಿ',
  not_attempted: 'ಪ್ರಯತ್ನ ಮಾಡಿಲ್ಲ.',
  need_label: 'ಬೇಕು',
  ready_label: 'ಸಿದ್ಧ!',
  score_label: 'ಸ್ಕೋರ್',
  certificate_for: 'ಗಾಗಿ ಪ್ರಮಾಣಪತ್ರ',
  downloaded_suffix: 'ಡೌನ್‌ಲೋಡ್ ಮಾಡಲಾಗಿದೆ!',
  cert_download_failed: 'ಪ್ರಮಾಣಪತ್ರವನ್ನು ರಚಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
  course_fallback: 'ಕೋರ್ಸ್',
  no_courses_enrolled: 'ಇನ್ನೂ ಯಾವುದೇ ಕೋರ್ಸ್‌ಗಳು ದಾಖಲಾಗಿಲ್ಲ',
  no_courses_enrolled_desc: 'ದಾಖಲಾದ ಕೋರ್ಸ್‌ಗಳು ಇಲ್ಲಿ ಗೋಚರಿಸುತ್ತವೆ ಆದ್ದರಿಂದ ನೀವು ಕಲಿಕೆಯನ್ನು ಮುಂದುವರಿಸಬಹುದು',
  continue_learning: 'ಕಲಿಯುವುದನ್ನು ಮುಂದುವರಿಸಿ',
  modules_label: 'ಮಾಡ್ಯೂಲ್‌ಗಳು.',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'ದಾಖಲಾಗಿದೆ.',
  enrollment_failed: 'ದಾಖಲಾತಿ ವಿಫಲವಾಗಿದೆ.',
  view_course: 'ಕೋರ್ಸ್ ವೀಕ್ಷಿಸಿ',
  kpi_need_80_certify: 'ಪ್ರಮಾಣೀಕರಿಸಲು ಕನಿಷ್ಠ 80 ಪ್ರತಿಶತ',
  kpi_no_attempts_yet: 'ಇನ್ನೂ ಯಾವುದೇ ಪ್ರಯತ್ನಗಳಿಲ್ಲ',
  courses_pending_suffix: 'ಕೋರ್ಸ್‌ಗಳು ಬಾಕಿ ಉಳಿದಿವೆ',
  module_marked_complete: 'ಮಾಡ್ಯೂಲ್ ಪೂರ್ಣಗೊಂಡಿದೆ ಎಂದು ಗುರುತಿಸಲಾಗಿದೆ!',
  failed_update_progress: 'ಪ್ರಗತಿಯನ್ನು ನವೀಕರಿಸಲು ವಿಫಲವಾಗಿದೆ.',
  no_downloadable_content: 'ಈ ಮಾಡ್ಯೂಲ್‌ಗೆ ಯಾವುದೇ ಡೌನ್‌ಲೋಡ್ ಮಾಡಬಹುದಾದ ವಿಷಯ ಲಭ್ಯವಿಲ್ಲ',
  download_started: 'ಡೌನ್‌ಲೋಡ್ ಪ್ರಾರಂಭವಾಗಿದೆ!',
  download_failed_retry: 'ಡೌನ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
  enrolled_loading_course: 'ನೋಂದಾಯಿಸಲಾಗಿದೆ! ಕೋರ್ಸ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ',
  topic_quiz_passed_toast: 'ವಿಷಯ ರಸಪ್ರಶ್ನೆ ಪಾಸಾಗಿದೆ!',
  score_saved_prefix: 'ಸ್ಕೋರ್ ಉಳಿಸಲಾಗಿದೆ.',
  need_80_to_pass: 'ಉತ್ತೀರ್ಣರಾಗಲು ಕನಿಷ್ಠ 80 ಪ್ರತಿಶತ ಬೇಕು.',
  failed_save_quiz_results: 'ರಸಪ್ರಶ್ನೆ ಫಲಿತಾಂಶಗಳನ್ನು ಉಳಿಸಲು ವಿಫಲವಾಗಿದೆ.',
  score_saved_cert_generated: 'ಸ್ಕೋರ್ ಉಳಿಸಲಾಗಿದೆ! ಗಾಗಿ ಪ್ರಮಾಣಪತ್ರವನ್ನು ರಚಿಸಲಾಗಿದೆ',
  need_80_for_cert: 'ಪ್ರಮಾಣಪತ್ರಕ್ಕಾಗಿ ಕನಿಷ್ಠ 80 ಪ್ರತಿಶತ ಅಗತ್ಯವಿದೆ.',
  no_course_selected: 'ಯಾವುದೇ ಕೋರ್ಸ್ ಆಯ್ಕೆ ಮಾಡಲಾಗಿಲ್ಲ. ದಯವಿಟ್ಟು ಹಿಂತಿರುಗಿ ಮತ್ತು ಕೋರ್ಸ್ ಆಯ್ಕೆಮಾಡಿ.',
  back_to_dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ಗೆ ಹಿಂತಿರುಗಿ.',
  need_enroll_message: 'ನೀವು ಅದರ ವೀಡಿಯೊಗಳನ್ನು ವೀಕ್ಷಿಸುವ ಅಥವಾ ಅದರ ರಸಪ್ರಶ್ನೆ ತೆಗೆದುಕೊಳ್ಳುವ ಮೊದಲು ನೀವು ಈ ಕೋರ್ಸ್‌ಗೆ ನೋಂದಾಯಿಸಿಕೊಳ್ಳಬೇಕು.',
  lessons_completed_suffix: 'ಪಾಠಗಳನ್ನು ಪೂರ್ಣಗೊಳಿಸಲಾಗಿದೆ',
  no_lessons_uploaded: 'ಇನ್ನೂ ಯಾವುದೇ ಪಾಠಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಲಾಗಿಲ್ಲ.',
  min_suffix: 'ನಿಮಿಷ',
  no_media_uploaded: 'ಈ ಪಾಠಕ್ಕಾಗಿ ಯಾವುದೇ ಮಾಧ್ಯಮವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಲಾಗಿಲ್ಲ',
  admin_trainer_upload_hint: 'ನಿರ್ವಾಹಕರು/ತರಬೇತುದಾರರು ಕೋರ್ಸ್ ನಿರ್ವಹಣಾ ಫಲಕದಿಂದ ವೀಡಿಯೊ/ಪಿಡಿಎಫ್ ಅನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಬಹುದು.',
  select_module_to_start: 'ಕಲಿಕೆಯನ್ನು ಪ್ರಾರಂಭಿಸಲು ಮಾಡ್ಯೂಲ್ ಆಯ್ಕೆಮಾಡಿ',
  video_label: 'ವೀಡಿಯೊ',
  pdf_label: 'PDF.',
  browser_no_video_support: 'ನಿಮ್ಮ ಬ್ರೌಸರ್ ವೀಡಿಯೊ ಟ್ಯಾಗ್ ಅನ್ನು ಬೆಂಬಲಿಸುವುದಿಲ್ಲ.',
  profile_email: 'ಇಮೇಲ್',
  profile_full_name: 'ಪೂರ್ಣ ಹೆಸರು',
  profile_phone: 'ಫೋನ್',
  profile_village: 'ಗ್ರಾಮ.',
  profile_district: 'ಜಿಲ್ಲೆ',
  profile_age: 'ವಯಸ್ಸು',
  profile_gender: 'ಲಿಂಗ',
  profile_city: 'ನಗರ',
  profile_language: 'ಭಾಷೆ.',
  profile_updated_toast: 'ಪ್ರೊಫೈಲ್ ಅನ್ನು ನವೀಕರಿಸಲಾಗಿದೆ',
  profile_update_failed: 'ಪ್ರೊಫೈಲ್ ಅನ್ನು ನವೀಕರಿಸಲು ವಿಫಲವಾಗಿದೆ',
  welcome_back_signing_in_as: 'ಮರಳಿ ಸ್ವಾಗತ! ನಿಮ್ಮನ್ನು ಹೀಗೆ ಸೈನ್ ಇನ್ ಮಾಡಲಾಗುತ್ತಿದೆ',
  account_created_signing_in: 'ಖಾತೆಯನ್ನು ರಚಿಸಲಾಗಿದೆ! ನಿಮ್ಮನ್ನು ಸೈನ್ ಇನ್ ಮಾಡಲಾಗುತ್ತಿದೆ',
  authentication_failed: 'ದೃಢೀಕರಣ ವಿಫಲವಾಗಿದೆ',
  invalid_credentials_use_demo: 'ಅಮಾನ್ಯ ಇಮೇಲ್ ಅಥವಾ ಪಾಸ್‌ವರ್ಡ್. ಕೆಳಗಿನ ಡೆಮೊ ಖಾತೆಗಳನ್ನು ಬಳಸಿ.',
  email_already_registered: 'ಈ ಇಮೇಲ್ ಈಗಾಗಲೇ ನೋಂದಾಯಿಸಲಾಗಿದೆ. ಬದಲಿಗೆ ಸೈನ್ ಇನ್ ಮಾಡಿ.',
  topic_otp_fraud: 'OTP ವಂಚನೆ',
  topic_upi_safety: 'UPI ಸುರಕ್ಷತೆ',
  topic_loan_fraud: 'ಸಾಲ ವಂಚನೆ.',
  topic_cyber_safety: 'ಸೈಬರ್ ಸುರಕ್ಷತೆ.',
  topic_kyc_basics: 'KYC ಬೇಸಿಕ್ಸ್',
  topic_digital_banking: 'ಡಿಜಿಟಲ್ ಬ್ಯಾಂಕಿಂಗ್',
  placeholder_min_8_chars: 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು',
  placeholder_your_password: 'ನಿಮ್ಮ ಗುಪ್ತಪದ',
  password_min_length_error: 'ಪಾಸ್ವರ್ಡ್ ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳಾಗಿರಬೇಕು',
  password_required_error: 'ಪಾಸ್ವರ್ಡ್ ಅಗತ್ಯವಿದೆ',
  placeholder_age_example: 'ಉದಾ. 28.',
  placeholder_your_city: 'ನಿಮ್ಮ ನಗರ.',
  placeholder_mobile_number: '10-ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  select_placeholder: 'ಆಯ್ಕೆ ಮಾಡಿ.',
  gender_male: 'ಪುರುಷ.',
  gender_female: 'ಹೆಣ್ಣು',
  gender_other: 'ಇತರೆ.',
  quiz_results_save_error: 'ನಿಮ್ಮ ರಸಪ್ರಶ್ನೆ ಫಲಿತಾಂಶಗಳನ್ನು ಉಳಿಸುವಲ್ಲಿ ಏನೋ ತಪ್ಪಾಗಿದೆ.',
  signed_out_success: 'ಯಶಸ್ವಿಯಾಗಿ ಸೈನ್ ಔಟ್ ಮಾಡಲಾಗಿದೆ',
  sign_out_failed: 'ಸೈನ್ ಔಟ್ ವಿಫಲವಾಗಿದೆ',
  expand_sidebar: 'ಸೈಡ್‌ಬಾರ್ ಅನ್ನು ವಿಸ್ತರಿಸಿ',
  collapse_sidebar: 'ಸೈಡ್‌ಬಾರ್ ಅನ್ನು ಸಂಕುಚಿಸಿ',
  switch_to_light_mode: 'ಲೈಟ್ ಮೋಡ್‌ಗೆ ಬದಲಿಸಿ',
  switch_to_dark_mode: 'ಡಾರ್ಕ್ ಮೋಡ್‌ಗೆ ಬದಲಿಸಿ.',


  // Signup form additions
  placeholder_your_full_name: 'ನಿಮ್ಮ ಪೂರ್ಣ ಹೆಸರು',
  mobile_number_label: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  placeholder_email_example: 'you@example.com.',
  copy_password_tooltip: 'Copy password',


  // Admin dashboard — our web properties
  our_properties_title: 'ನಮ್ಮ ವೆಬ್ ಗುಣಲಕ್ಷಣಗಳು',

  property_main_site_label: 'ಫೀಡ್ ಫೌಂಡೇಶನ್',
  property_main_site_full_title: 'ಸಮಾನತೆ, ಸಮಾನತೆ ಮತ್ತು ಘನತೆಯ ಅಡಿಪಾಯ',

  property_flc_site_label: 'CMS-ಫೀಡ್ FLC',
  property_flc_site_full_title: 'CMS-FEED ಹಣಕಾಸು ಸಾಕ್ಷರತಾ ಕೇಂದ್ರ',

  login_email_or_phone: 'ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  placeholder_email_or_phone: 'you@example.com ಅಥವಾ 10-ಅಂಕಿಯ ಸಂಖ್ಯೆ',
  login_email_required_error: 'ಇಮೇಲ್ ಅಗತ್ಯವಿದೆ',
  login_identifier_required_error: 'ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಗತ್ಯವಿದೆ',

  phone_invalid_error: 'ಮಾನ್ಯವಾದ 10-ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ',
  phone_required_error: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಗತ್ಯವಿದೆ.',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'ನೋಂದಾಯಿತ',
  login_forgot_password: 'ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?',
  fp_title: 'ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿದ್ದೀರಿ',
  fp_subtitle: 'ನಿಮ್ಮ ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ನಮೂದಿಸಿ, ನಾವು ನಿಮಗೆ 6 ಅಂಕಿಯ OTP ಇಮೇಲ್ ಮಾಡುತ್ತೇವೆ.',
  fp_identifier_placeholder: 'ಇಮೇಲ್ ಅಥವಾ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  fp_send_otp: 'OTP ಕಳುಹಿಸಿ',
  fp_otp_sent: 'ಖಾತೆ ಇದ್ದರೆ, ಅದರ ಇಮೇಲ್‌ಗೆ OTP ಕಳುಹಿಸಲಾಗಿದೆ. ಅದನ್ನು ಹೊಸ ಪಾಸ್‌ವರ್ಡ್‌ನೊಂದಿಗೆ ಕೆಳಗೆ ನಮೂದಿಸಿ. OTP 10 ನಿಮಿಷ ಮಾನ್ಯವಾಗಿರುತ್ತದೆ.',
  fp_otp_placeholder: '6 ಅಂಕಿಯ OTP',
  fp_new_password: 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ (ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು)',
  fp_confirm_password: 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್ ದೃಢೀಕರಿಸಿ',
  fp_reset_password: 'ಪಾಸ್‌ವರ್ಡ್ ಮರುಹೊಂದಿಸಿ',
  fp_resend_otp: 'OTP ಮತ್ತೆ ಕಳುಹಿಸಿ',
  fp_done: 'ನಿಮ್ಮ ಪಾಸ್‌ವರ್ಡ್ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ.',
  fp_pw_min: 'ಪಾಸ್‌ವರ್ಡ್ ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳಿರಬೇಕು.',
  fp_pw_mismatch: 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ.',
  fp_back_to_signin: 'ಸೈನ್ ಇನ್‌ಗೆ ಹಿಂತಿರುಗಿ',
  not_found_title: 'ಪುಟ ಕಂಡುಬಂದಿಲ್ಲ',
  not_found_desc: 'ನೀವು ಹುಡುಕುತ್ತಿರುವ ಪುಟ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ. ನಿಮ್ಮನ್ನು ಹಿಂದಕ್ಕೆ ಕರೆದೊಯ್ಯೋಣ!',
  go_back: 'ಹಿಂದೆ ಹೋಗಿ',
  back_to_home: 'ಮುಖಪುಟಕ್ಕೆ ಹಿಂತಿರುಗಿ',
  trainer_my_courses: 'ನನ್ನ ಕೋರ್ಸ್‌ಗಳು',
  trainer_courses_by_you: 'ನೀವು ರಚಿಸಿದ ಕೋರ್ಸ್(ಗಳು)',
  trainer_add_course: 'ಕೋರ್ಸ್ ಸೇರಿಸಿ',
  trainer_modules_count: 'ಮಾಡ್ಯೂಲ್(ಗಳು)',
  trainer_manage_modules: 'ವೀಡಿಯೊ/ಮಾಡ್ಯೂಲ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ',
  trainer_edit_course: 'ಕೋರ್ಸ್ ಸಂಪಾದಿಸಿ',
  trainer_delete_course: 'ಕೋರ್ಸ್ ಅಳಿಸಿ',
  trainer_completion_overview: 'ಕೋರ್ಸ್ ಪೂರ್ಣಗೊಳಿಸುವಿಕೆ ಅವಲೋಕನ',
  login_initiative: 'FEED — ಸಮಾನತೆ, ನ್ಯಾಯ ಮತ್ತು ಘನತೆಗಾಗಿ ಫೌಂಡೇಶನ್ & CMS ಫೌಂಡೇಶನ್‌ನ ಜಂಟಿ ಉಪಕ್ರಮ',
};

const bn: Dict = {
  save: 'সংরক্ষণ করুন', save_changes: 'পরিবর্তন সংরক্ষণ করুন', cancel: 'বাতিল করুন', delete: 'মুছে ফেলুন',
  edit: 'সম্পাদনা করুন', add: 'যোগ করুন', search: 'অনুসন্ধান করুন', close: 'বন্ধ করুন', loading: 'লোড হচ্ছে…',
  saving: 'সংরক্ষণ হচ্ছে…', refresh: 'রিফ্রেশ করুন', back: 'পিছনে', continue: 'চালিয়ে যান', enroll: 'ভর্তি হন',
  enrolling: 'ভর্তি হচ্ছে…', download: 'ডাউনলোড', sign_out: 'সাইন আউট', signing_out: 'সাইন আউট হচ্ছে…',
  my_profile: 'আমার প্রোফাইল', settings: 'সেটিংস', nav_dashboard: 'ড্যাশবোর্ড', nav_reports: 'রিপোর্ট',
  nav_courses: 'কোর্স', nav_course_management: 'কোর্স ব্যবস্থাপনা', nav_media: 'ভিডিও ও মিডিয়া',
  nav_assessments: 'মূল্যায়ন', nav_members: 'সদস্যরা', nav_trainers: 'প্রশিক্ষকরা', nav_certificates: 'সার্টিফিকেট',
  nav_notifications: 'বিজ্ঞপ্তি', nav_settings: 'সেটিংস', nav_learner_progress: 'শিক্ষার্থীর অগ্রগতি',
  nav_my_dashboard: 'আমার ড্যাশবোর্ড', nav_my_courses: 'আমার কোর্স', nav_help: 'সহায়তা', group_overview: 'সংক্ষিপ্ত বিবরণ',
  group_content: 'বিষয়বস্তু', group_people: 'মানুষ', group_system: 'সিস্টেম', group_my_work: 'আমার কাজ',
  group_learning: 'শেখা', group_support: 'সহায়তা', role_admin: 'অ্যাডমিন — FEED HQ', role_trainer: 'ফিল্ড প্রশিক্ষক',
  role_learner: 'শিক্ষার্থী', login_welcome_back: 'ফিরে আসার জন্য স্বাগতম', login_create_account: 'অ্যাকাউন্ট তৈরি করুন',
  login_signin_subtitle: 'আপনার আর্থিক সাক্ষরতার যাত্রা চালিয়ে যেতে সাইন ইন করুন',
  login_signup_subtitle: 'RBI কেহতা হ্যায় প্রশিক্ষণ কর্মসূচিতে যোগ দিন', login_i_am_a: 'আমি একজন',
  login_role_learner: 'শিক্ষার্থী', login_role_trainer: 'ফিল্ড প্রশিক্ষক', login_role_admin: 'অ্যাডমিন',
  login_full_name: 'পুরো নাম', login_email: 'ইমেল ঠিকানা', login_password: 'পাসওয়ার্ড', login_sign_in: 'সাইন ইন',
  login_sign_up: 'অ্যাকাউন্ট তৈরি করুন', login_signing_in: 'সাইন ইন হচ্ছে…', login_creating_account: 'অ্যাকাউন্ট তৈরি হচ্ছে…',
  login_no_account: 'অ্যাকাউন্ট নেই? ', login_have_account: 'ইতিমধ্যে অ্যাকাউন্ট আছে? ', login_toggle_signup: 'সাইন আপ',
  login_toggle_signin: 'সাইন ইন', login_demo_accounts: '🔒 ডেমো অ্যাকাউন্ট — পূরণ করতে ক্লিক করুন',
  login_hero_tagline: 'RBI কেহতা হ্যায় প্রচারাভিযান',
  login_hero_desc: 'শিক্ষার্থী, সম্প্রদায়ের সদস্য এবং ফিল্ড স্টাফদের জন্য আর্থিক সাক্ষরতা প্রশিক্ষণ — OTP জালিয়াতি, UPI নিরাপত্তা, ঋণ জালিয়াতি এবং সাইবার নিরাপত্তা।',
  admin_dashboard_title: 'প্রশিক্ষণ প্রভাব ড্যাশবোর্ড', admin_dashboard_subtitle: 'RBI কেহতা হ্যায় — FEED ও CMS ফাউন্ডেশন',
  export_report: 'রিপোর্ট এক্সপোর্ট করুন', filter: 'ফিল্টার', this_week: 'এই সপ্তাহ', this_month: 'এই মাস',
  last_3_months: 'গত ৩ মাস', all_time: 'সর্বকাল', kpi_members_trained: 'প্রশিক্ষিত সদস্য',
  kpi_members_trained_sub: 'মোট নিবন্ধিত শিক্ষার্থী', kpi_certificates_issued: 'জারিকৃত সার্টিফিকেট',
  kpi_all_time: 'সর্বকাল', kpi_members_pending: 'অপেক্ষমাণ সদস্য', kpi_members_pending_sub: 'এখনও ভর্তি হয়নি',
  kpi_pass_rate: 'সামগ্রিক পাস হার', kpi_districts_covered: 'কভার করা জেলা', kpi_districts_covered_sub: 'সকল শিক্ষার্থীর মধ্যে',
  kpi_active_courses: 'সক্রিয় কোর্স', kpi_active_courses_sub: 'প্রকাশিত ও উপলব্ধ',
  trainer_dashboard_title: 'প্রশিক্ষক ড্যাশবোর্ড', trainer_courses_created: 'তৈরি করা কোর্স',
  trainer_total_learners: 'মোট শিক্ষার্থী', trainer_avg_completion: 'গড় সমাপ্তি', trainer_certs_issued: 'জারিকৃত সার্টিফিকেট',
  learner_namaste: 'নমস্তে', learner_dashboard_subtitle: 'আপনার RBI কেহতা হ্যায় আর্থিক সাক্ষরতা প্রশিক্ষণ চালিয়ে যান',
  learner_courses_enrolled: 'ভর্তি হওয়া কোর্স', learner_modules_completed: 'সম্পন্ন মডিউল', learner_avg_quiz_score: 'গড় কুইজ স্কোর',
  learner_certificates_earned: 'অর্জিত সার্টিফিকেট', my_courses_title: 'আমার কোর্স',
  my_courses_subtitle: 'যেখানে ছেড়েছিলেন সেখান থেকে চালিয়ে যান, অথবা নীচে নতুন কোর্সে ভর্তি হন',
  browse_courses: 'কোর্স ব্রাউজ করুন', continue_course: 'কোর্স চালিয়ে যান', take_final_quiz: 'চূড়ান্ত কুইজ দিন',
  lessons: 'পাঠ', topics: 'বিষয়', course_progress: 'কোর্সের অগ্রগতি', mark_complete: 'সম্পন্ন হিসাবে চিহ্নিত করুন',
  completed: 'সম্পন্ন', previous: 'পূর্ববর্তী', next: 'পরবর্তী', enroll_now: 'এখনই ভর্তি হন',
  take_topic_quiz: 'বিষয় কুইজ দিন', topic_quiz_passed: 'কুইজ উত্তীর্ণ',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'কুইজ লোড হচ্ছে',
  quiz_fetching_questions: 'অবশ্যই থেকে প্রশ্ন আনা',
  quiz_title: 'কুইজ',
  quiz_no_questions_course: 'এই কোর্সের জন্য এখনও কোন কুইজ প্রশ্ন উপলব্ধ নেই।',
  quiz_no_questions_topic: 'এই বিষয়ের জন্য এখনো কোনো কুইজ প্রশ্ন উপলব্ধ নেই।',
  quiz_results_title: 'কুইজের ফলাফল।',
  quiz_question_prefix: 'কুইজ, প্রশ্ন',
  of: 'এর',
  financial_literacy_quiz: 'আর্থিক সাক্ষরতা কুইজ.',
  show_hint: 'ইঙ্গিত দেখান',
  hide_explanation: 'ব্যাখ্যা লুকান',
  submit_quiz: 'কুইজ জমা দিন',
  select_answer_warning: 'চালিয়ে যাওয়ার আগে একটি উত্তর নির্বাচন করুন.',
  you_answered: 'আপনি উত্তর দিয়েছেন',
  out_of: 'এর বাইরে',
  correctly: 'সঠিকভাবে',
  quiz_congrats: 'অভিনন্দন! আপনি পাস করেছেন!',
  quiz_cert_generated_note: 'Your certificate has been generated. Download it from the Certificates panel.',
  quiz_need_80: 'একটি শংসাপত্র অর্জন করতে আপনার কমপক্ষে 80 শতাংশ প্রয়োজন',
  quiz_review_retry: 'উপাদান পর্যালোচনা করুন এবং আপনার শংসাপত্র অর্জন করতে আবার চেষ্টা করুন.',
  correct_answer_label: 'সঠিক',
  retry: 'আবার চেষ্টা করুন',
  done: 'সম্পন্ন',
  my_certificates: 'আমার সার্টিফিকেট.',
  pending_certificates: 'মুলতুবি সার্টিফিকেট',
  download_pdf: 'PDF ডাউনলোড করুন।',
  generating: 'উৎপন্ন হচ্ছে',
  no_certificates_yet: 'এখনো কোনো সার্টিফিকেট নেই',
  score_80_hint: 'একটি উপার্জন করতে একটি কুইজে কমপক্ষে 80 শতাংশ স্কোর করুন',
  not_attempted: 'চেষ্টা করা হয়নি',
  need_label: 'প্রয়োজন.',
  ready_label: 'প্রস্তুত!',
  score_label: 'স্কোর।',
  certificate_for: 'জন্য সার্টিফিকেট',
  downloaded_suffix: 'ডাউনলোড করা হয়েছে!',
  cert_download_failed: 'Failed to generate certificate. Please try again.',
  course_fallback: 'কোর্স',
  no_courses_enrolled: 'কোন কোর্স এখনো নথিভুক্ত',
  no_courses_enrolled_desc: 'নথিভুক্ত কোর্সগুলি এখানে প্রদর্শিত হবে যাতে আপনি শেখা চালিয়ে যেতে পারেন',
  continue_learning: 'শেখা চালিয়ে যান।',
  modules_label: 'মডিউল',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'ভর্তি হয়েছে।',
  enrollment_failed: 'তালিকাভুক্তি ব্যর্থ হয়েছে.',
  view_course: 'কোর্স দেখুন',
  kpi_need_80_certify: 'কমপক্ষে 80 শতাংশ প্রত্যয়িত।',
  kpi_no_attempts_yet: 'এখনও কোন প্রচেষ্টা',
  courses_pending_suffix: 'মুলতুবি কোর্স',
  module_marked_complete: 'মডিউল সম্পূর্ণ হিসাবে চিহ্নিত!',
  failed_update_progress: 'অগ্রগতি আপডেট করতে ব্যর্থ হয়েছে৷',
  no_downloadable_content: 'এই মডিউলের জন্য কোন ডাউনলোডযোগ্য সামগ্রী উপলব্ধ নেই।',
  download_started: 'ডাউনলোড শুরু!',
  download_failed_retry: 'ডাউনলোড ব্যর্থ হয়েছে৷ আবার চেষ্টা করুন.',
  enrolled_loading_course: 'নথিভুক্ত ! কোর্স লোড হচ্ছে',
  topic_quiz_passed_toast: 'বিষয় কুইজ পাস!',
  score_saved_prefix: 'স্কোর সংরক্ষিত',
  need_80_to_pass: 'পাস করতে কমপক্ষে ৮০ শতাংশ লাগবে।',
  failed_save_quiz_results: 'কুইজের ফলাফল সংরক্ষণ করতে ব্যর্থ হয়েছে৷',
  score_saved_cert_generated: 'স্কোর সংরক্ষিত! এর জন্য শংসাপত্র তৈরি করা হয়েছে।',
  need_80_for_cert: 'সার্টিফিকেটের জন্য কমপক্ষে 80 শতাংশ প্রয়োজন।',
  no_course_selected: 'কোন কোর্স সিলেক্ট করা হয়নি। অনুগ্রহ করে ফিরে যান এবং একটি কোর্স নির্বাচন করুন।',
  back_to_dashboard: 'ড্যাশবোর্ডে ফিরে যান।',
  need_enroll_message: 'আপনি এই কোর্সের ভিডিওগুলি দেখতে বা এর কুইজে অংশগ্রহণ করার আগে আপনাকে এই কোর্সে নথিভুক্ত করতে হবে৷',
  lessons_completed_suffix: 'পাঠ সমাপ্ত',
  no_lessons_uploaded: 'কোন পাঠ এখনো আপলোড',
  min_suffix: 'মিনিট',
  no_media_uploaded: 'এই পাঠের জন্য কোনো মিডিয়া আপলোড করা হয়নি',
  admin_trainer_upload_hint: 'প্রশাসক/প্রশিক্ষক কোর্স ম্যানেজমেন্ট প্যানেল থেকে ভিডিও/পিডিএফ আপলোড করতে পারেন।',
  select_module_to_start: 'শেখা শুরু করতে একটি মডিউল নির্বাচন করুন',
  video_label: 'ভিডিও',
  pdf_label: 'PDF.',
  browser_no_video_support: 'আপনার ব্রাউজার ভিডিও ট্যাগ সমর্থন করে না.',
  profile_email: 'ইমেইল',
  profile_full_name: 'পুরো নাম',
  profile_phone: 'ফোন',
  profile_village: 'গ্রাম',
  profile_district: 'জেলা',
  profile_age: 'বয়স।',
  profile_gender: 'লিঙ্গ',
  profile_city: 'শহর',
  profile_language: 'ভাষা',
  profile_updated_toast: 'প্রোফাইল আপডেট করা হয়েছে।',
  profile_update_failed: 'প্রোফাইল আপডেট করতে ব্যর্থ হয়েছে.',
  welcome_back_signing_in_as: 'আবার স্বাগতম! হিসাবে আপনাকে সাইন ইন করা হচ্ছে.',
  account_created_signing_in: 'অ্যাকাউন্ট তৈরি! আপনাকে সাইন ইন করা হচ্ছে',
  authentication_failed: 'প্রমাণীকরণ ব্যর্থ হয়েছে৷',
  invalid_credentials_use_demo: 'অবৈধ ইমেল বা পাসওয়ার্ড। নীচের ডেমো অ্যাকাউন্ট ব্যবহার করুন.',
  email_already_registered: 'This email is already registered. Please sign in instead.',
  topic_otp_fraud: 'ওটিপি জালিয়াতি',
  topic_upi_safety: 'UPI নিরাপত্তা',
  topic_loan_fraud: 'ঋণ জালিয়াতি',
  topic_cyber_safety: 'সাইবার নিরাপত্তা',
  topic_kyc_basics: 'কেওয়াইসি বেসিক।',
  topic_digital_banking: 'ডিজিটাল ব্যাংকিং',
  placeholder_min_8_chars: 'ন্যূনতম ৮ অক্ষর।',
  placeholder_your_password: 'আপনার পাসওয়ার্ড',
  password_min_length_error: 'পাসওয়ার্ড কমপক্ষে 8 অক্ষরের হতে হবে।',
  password_required_error: 'পাসওয়ার্ড প্রয়োজন',
  placeholder_age_example: 'যেমন 28',
  placeholder_your_city: 'তোমার শহর।',
  placeholder_mobile_number: '10-সংখ্যার মোবাইল নম্বর।',
  select_placeholder: 'নির্বাচন করুন',
  gender_male: 'পুরুষ',
  gender_female: 'মহিলা',
  gender_other: 'অন্যান্য',
  quiz_results_save_error: 'আপনার কুইজের ফলাফল সংরক্ষণ করতে কিছু ভুল হয়েছে।',
  signed_out_success: 'সফলভাবে সাইন আউট হয়েছে৷',
  sign_out_failed: 'সাইন আউট ব্যর্থ হয়েছে',
  expand_sidebar: 'সাইডবার প্রসারিত করুন।',
  collapse_sidebar: 'সাইডবার সঙ্কুচিত করুন।',
  switch_to_light_mode: 'হালকা মোডে স্যুইচ করুন',
  switch_to_dark_mode: 'ডার্ক মোডে স্যুইচ করুন',


  // Signup form additions
  placeholder_your_full_name: 'আপনার পুরো নাম',
  mobile_number_label: 'মোবাইল নম্বর',
  placeholder_email_example: 'you@example.com।',
  copy_password_tooltip: 'পাসওয়ার্ড কপি করুন।',


  // Admin dashboard — our web properties
  our_properties_title: 'আমাদের ওয়েব বৈশিষ্ট্য',

  property_main_site_label: 'ফিড ফাউন্ডেশন',
  property_main_site_full_title: 'সমতা, ইক্যুইটি এবং মর্যাদার জন্য ফাউন্ডেশন',

  property_flc_site_label: 'সিএমএস-ফিড এফএলসি',
  property_flc_site_full_title: 'CMS-FEED আর্থিক সাক্ষরতা কেন্দ্র',

  login_email_or_phone: 'ইমেইল বা মোবাইল নম্বর',
  placeholder_email_or_phone: 'you@example.com বা 10-সংখ্যার নম্বর',
  login_email_required_error: 'ইমেল প্রয়োজন',
  login_identifier_required_error: 'ইমেল বা মোবাইল নম্বর প্রয়োজন',

  phone_invalid_error: 'একটি বৈধ 10-সংখ্যার মোবাইল নম্বর লিখুন৷',
  phone_required_error: 'মোবাইল নম্বর প্রয়োজন',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'নথিভুক্ত',
  login_forgot_password: 'পাসওয়ার্ড ভুলে গেছেন?',
  fp_title: 'পাসওয়ার্ড ভুলে গেছেন',
  fp_subtitle: 'আপনার ইমেল বা মোবাইল নম্বর দিন, আমরা আপনাকে ৬ সংখ্যার OTP ইমেল করব।',
  fp_identifier_placeholder: 'ইমেল বা মোবাইল নম্বর',
  fp_send_otp: 'OTP পাঠান',
  fp_otp_sent: 'অ্যাকাউন্ট থাকলে তার ইমেলে OTP পাঠানো হয়েছে। নতুন পাসওয়ার্ডসহ নিচে তা দিন। OTP ১০ মিনিট বৈধ।',
  fp_otp_placeholder: '৬ সংখ্যার OTP',
  fp_new_password: 'নতুন পাসওয়ার্ড (কমপক্ষে ৮ অক্ষর)',
  fp_confirm_password: 'নতুন পাসওয়ার্ড নিশ্চিত করুন',
  fp_reset_password: 'পাসওয়ার্ড রিসেট করুন',
  fp_resend_otp: 'OTP আবার পাঠান',
  fp_done: 'আপনার পাসওয়ার্ড আপডেট হয়েছে।',
  fp_pw_min: 'পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে।',
  fp_pw_mismatch: 'পাসওয়ার্ড মিলছে না।',
  fp_back_to_signin: 'সাইন ইনে ফিরে যান',
  not_found_title: 'পেজ পাওয়া যায়নি',
  not_found_desc: 'আপনি যে পেজটি খুঁজছেন তা নেই। চলুন আপনাকে ফিরিয়ে নিয়ে যাই!',
  go_back: 'ফিরে যান',
  back_to_home: 'হোমে ফিরে যান',
  trainer_my_courses: 'আমার কোর্স',
  trainer_courses_by_you: 'আপনার তৈরি কোর্স',
  trainer_add_course: 'কোর্স যোগ করুন',
  trainer_modules_count: 'মডিউল',
  trainer_manage_modules: 'ভিডিও/মডিউল পরিচালনা করুন',
  trainer_edit_course: 'কোর্স সম্পাদনা করুন',
  trainer_delete_course: 'কোর্স মুছুন',
  trainer_completion_overview: 'কোর্স সম্পূর্ণতার সারসংক্ষেপ',
  login_initiative: 'FEED — সমতা, ইকুইটি ও মর্যাদার জন্য ফাউন্ডেশন ও CMS ফাউন্ডেশনের যৌথ উদ্যোগ',
};

const ml: Dict = {
  save: 'സേവ് ചെയ്യുക', save_changes: 'മാറ്റങ്ങൾ സേവ് ചെയ്യുക', cancel: 'റദ്ദാക്കുക', delete: 'ഇല്ലാതാക്കുക',
  edit: 'തിരുത്തുക', add: 'ചേർക്കുക', search: 'തിരയുക', close: 'അടയ്ക്കുക', loading: 'ലോഡ് ചെയ്യുന്നു…',
  saving: 'സേവ് ചെയ്യുന്നു…', refresh: 'പുതുക്കുക', back: 'തിരികെ', continue: 'തുടരുക', enroll: 'ചേരുക',
  enrolling: 'ചേരുന്നു…', download: 'ഡൗൺലോഡ്', sign_out: 'സൈൻ ഔട്ട്', signing_out: 'സൈൻ ഔട്ട് ചെയ്യുന്നു…',
  my_profile: 'എന്റെ പ്രൊഫൈൽ', settings: 'ക്രമീകരണങ്ങൾ', nav_dashboard: 'ഡാഷ്‌ബോർഡ്', nav_reports: 'റിപ്പോർട്ടുകൾ',
  nav_courses: 'കോഴ്സുകൾ', nav_course_management: 'കോഴ്സ് മാനേജ്‌മെന്റ്', nav_media: 'വീഡിയോകളും മീഡിയയും',
  nav_assessments: 'വിലയിരുത്തലുകൾ', nav_members: 'അംഗങ്ങൾ', nav_trainers: 'പരിശീലകർ', nav_certificates: 'സർട്ടിഫിക്കറ്റുകൾ',
  nav_notifications: 'അറിയിപ്പുകൾ', nav_settings: 'ക്രമീകരണങ്ങൾ', nav_learner_progress: 'പഠിതാവിന്റെ പുരോഗതി',
  nav_my_dashboard: 'എന്റെ ഡാഷ്‌ബോർഡ്', nav_my_courses: 'എന്റെ കോഴ്സുകൾ', nav_help: 'സഹായം', group_overview: 'അവലോകനം',
  group_content: 'ഉള്ളടക്കം', group_people: 'ആളുകൾ', group_system: 'സിസ്റ്റം', group_my_work: 'എന്റെ ജോലി',
  group_learning: 'പഠനം', group_support: 'പിന്തുണ', role_admin: 'അഡ്മിൻ — FEED HQ', role_trainer: 'ഫീൽഡ് പരിശീലകൻ',
  role_learner: 'പഠിതാവ്', login_welcome_back: 'തിരികെ സ്വാഗതം', login_create_account: 'അക്കൗണ്ട് സൃഷ്ടിക്കുക',
  login_signin_subtitle: 'നിങ്ങളുടെ സാമ്പത്തിക സാക്ഷരതാ യാത്ര തുടരാൻ സൈൻ ഇൻ ചെയ്യുക',
  login_signup_subtitle: 'RBI കെഹ്താ ഹേ പരിശീലന പരിപാടിയിൽ ചേരുക', login_i_am_a: 'ഞാൻ ഒരു',
  login_role_learner: 'പഠിതാവ്', login_role_trainer: 'ഫീൽഡ് പരിശീലകൻ', login_role_admin: 'അഡ്മിൻ',
  login_full_name: 'മുഴുവൻ പേര്', login_email: 'ഇമെയിൽ വിലാസം', login_password: 'പാസ്‌വേഡ്', login_sign_in: 'സൈൻ ഇൻ',
  login_sign_up: 'അക്കൗണ്ട് സൃഷ്ടിക്കുക', login_signing_in: 'സൈൻ ഇൻ ചെയ്യുന്നു…', login_creating_account: 'അക്കൗണ്ട് സൃഷ്ടിക്കുന്നു…',
  login_no_account: 'അക്കൗണ്ട് ഇല്ലേ? ', login_have_account: 'ഇതിനകം അക്കൗണ്ട് ഉണ്ടോ? ', login_toggle_signup: 'സൈൻ അപ്പ്',
  login_toggle_signin: 'സൈൻ ഇൻ', login_demo_accounts: '🔒 ഡെമോ അക്കൗണ്ടുകൾ — പൂരിപ്പിക്കാൻ ക്ലിക്ക് ചെയ്യുക',
  login_hero_tagline: 'RBI കെഹ്താ ഹേ ക്യാമ്പെയിൻ',
  login_hero_desc: 'പഠിതാക്കൾ, സമൂഹാംഗങ്ങൾ, ഫീൽഡ് സ്റ്റാഫ് എന്നിവർക്കുള്ള സാമ്പത്തിക സാക്ഷരതാ പരിശീലനം — OTP തട്ടിപ്പ്, UPI സുരക്ഷ, വായ്പാ തട്ടിപ്പ്, സൈബർ സുരക്ഷ.',
  admin_dashboard_title: 'പരിശീലന ഇംപാക്ട് ഡാഷ്‌ബോർഡ്', admin_dashboard_subtitle: 'RBI കെഹ്താ ഹേ — FEED & CMS ഫൗണ്ടേഷൻ',
  export_report: 'റിപ്പോർട്ട് എക്സ്പോർട്ട് ചെയ്യുക', filter: 'ഫിൽട്ടർ', this_week: 'ഈ ആഴ്ച', this_month: 'ഈ മാസം',
  last_3_months: 'കഴിഞ്ഞ 3 മാസം', all_time: 'എല്ലാ കാലവും', kpi_members_trained: 'പരിശീലനം നേടിയ അംഗങ്ങൾ',
  kpi_members_trained_sub: 'ആകെ രജിസ്റ്റർ ചെയ്ത പഠിതാക്കൾ', kpi_certificates_issued: 'നൽകിയ സർട്ടിഫിക്കറ്റുകൾ',
  kpi_all_time: 'എല്ലാ കാലവും', kpi_members_pending: 'തീർപ്പാക്കാത്ത അംഗങ്ങൾ', kpi_members_pending_sub: 'ഇതുവരെ ചേർന്നിട്ടില്ല',
  kpi_pass_rate: 'മൊത്തം വിജയ നിരക്ക്', kpi_districts_covered: 'ഉൾപ്പെടുത്തിയ ജില്ലകൾ', kpi_districts_covered_sub: 'എല്ലാ പഠിതാക്കളിലും',
  kpi_active_courses: 'സജീവ കോഴ്സുകൾ', kpi_active_courses_sub: 'പ്രസിദ്ധീകരിച്ചതും ലഭ്യവുമാണ്',
  trainer_dashboard_title: 'പരിശീലക ഡാഷ്‌ബോർഡ്', trainer_courses_created: 'സൃഷ്ടിച്ച കോഴ്സുകൾ',
  trainer_total_learners: 'ആകെ പഠിതാക്കൾ', trainer_avg_completion: 'ശരാശരി പൂർത്തീകരണം', trainer_certs_issued: 'നൽകിയ സർട്ടിഫിക്കറ്റുകൾ',
  learner_namaste: 'നമസ്തേ', learner_dashboard_subtitle: 'നിങ്ങളുടെ RBI കെഹ്താ ഹേ സാമ്പത്തിക സാക്ഷരതാ പരിശീലനം തുടരുക',
  learner_courses_enrolled: 'ചേർന്ന കോഴ്സുകൾ', learner_modules_completed: 'പൂർത്തിയാക്കിയ മൊഡ്യൂളുകൾ',
  learner_avg_quiz_score: 'ശരാശരി ക്വിസ് സ്കോർ', learner_certificates_earned: 'നേടിയ സർട്ടിഫിക്കറ്റുകൾ',
  my_courses_title: 'എന്റെ കോഴ്സുകൾ', my_courses_subtitle: 'നിർത്തിയിടത്ത് നിന്ന് തുടരുക, അല്ലെങ്കിൽ താഴെ പുതിയ കോഴ്സിൽ ചേരുക',
  browse_courses: 'കോഴ്സുകൾ ബ്രൗസ് ചെയ്യുക', continue_course: 'കോഴ്സ് തുടരുക', take_final_quiz: 'അവസാന ക്വിസ് എടുക്കുക',
  lessons: 'പാഠങ്ങൾ', topics: 'വിഷയങ്ങൾ', course_progress: 'കോഴ്സ് പുരോഗതി', mark_complete: 'പൂർത്തിയായി അടയാളപ്പെടുത്തുക',
  completed: 'പൂർത്തിയായി', previous: 'മുമ്പത്തെ', next: 'അടുത്തത്', enroll_now: 'ഇപ്പോൾ ചേരുക',
  take_topic_quiz: 'വിഷയ ക്വിസ് എടുക്കുക', topic_quiz_passed: 'ക്വിസ് വിജയിച്ചു',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'ക്വിസ് ലോഡ് ചെയ്യുന്നു',
  quiz_fetching_questions: 'കോഴ്‌സിൽ നിന്ന് ചോദ്യങ്ങൾ എടുക്കുന്നു.',
  quiz_title: 'ക്വിസ്',
  quiz_no_questions_course: 'ഈ കോഴ്‌സിന് ഇതുവരെ ക്വിസ് ചോദ്യങ്ങളൊന്നും ലഭ്യമല്ല.',
  quiz_no_questions_topic: 'No quiz questions available for this topic yet.',
  quiz_results_title: 'ക്വിസ് ഫലങ്ങൾ',
  quiz_question_prefix: 'ക്വിസ്, ചോദ്യം',
  of: 'ൻ്റെ',
  financial_literacy_quiz: 'സാമ്പത്തിക സാക്ഷരതാ ക്വിസ്',
  show_hint: 'സൂചന കാണിക്കുക',
  hide_explanation: 'വിശദീകരണം മറയ്ക്കുക.',
  submit_quiz: 'ക്വിസ് സമർപ്പിക്കുക.',
  select_answer_warning: 'തുടരുന്നതിന് മുമ്പ് ദയവായി ഒരു ഉത്തരം തിരഞ്ഞെടുക്കുക.',
  you_answered: 'നിങ്ങൾ ഉത്തരം പറഞ്ഞു.',
  out_of: 'പുറത്ത്',
  correctly: 'ശരിയായി',
  quiz_congrats: 'അഭിനന്ദനങ്ങൾ! നിങ്ങൾ കടന്നുപോയി!',
  quiz_cert_generated_note: 'നിങ്ങളുടെ സർട്ടിഫിക്കറ്റ് സൃഷ്ടിച്ചു. സർട്ടിഫിക്കറ്റ് പാനലിൽ നിന്ന് ഇത് ഡൗൺലോഡ് ചെയ്യുക.',
  quiz_need_80: 'ഒരു സർട്ടിഫിക്കറ്റ് നേടുന്നതിന് നിങ്ങൾക്ക് കുറഞ്ഞത് 80 ശതമാനമെങ്കിലും ആവശ്യമാണ്',
  quiz_review_retry: 'മെറ്റീരിയൽ അവലോകനം ചെയ്‌ത് നിങ്ങളുടെ സർട്ടിഫിക്കറ്റ് നേടാൻ വീണ്ടും ശ്രമിക്കുക.',
  correct_answer_label: 'ശരിയാണ്.',
  retry: 'വീണ്ടും ശ്രമിക്കുക',
  done: 'ചെയ്തു',
  my_certificates: 'എൻ്റെ സർട്ടിഫിക്കറ്റുകൾ.',
  pending_certificates: 'ശേഷിക്കുന്ന സർട്ടിഫിക്കറ്റുകൾ',
  download_pdf: 'PDF ഡൗൺലോഡ് ചെയ്യുക',
  generating: 'സൃഷ്ടിക്കുന്നു',
  no_certificates_yet: 'ഇതുവരെ സർട്ടിഫിക്കറ്റുകളൊന്നുമില്ല',
  score_80_hint: 'ഒന്ന് സമ്പാദിക്കാൻ ഒരു ക്വിസിൽ കുറഞ്ഞത് 80 ശതമാനമെങ്കിലും സ്കോർ ചെയ്യുക',
  not_attempted: 'ശ്രമിച്ചിട്ടില്ല.',
  need_label: 'ആവശ്യം',
  ready_label: 'തയ്യാറാണ്!',
  score_label: 'സ്കോർ',
  certificate_for: 'വേണ്ടിയുള്ള സർട്ടിഫിക്കറ്റ്',
  downloaded_suffix: 'ഡൗൺലോഡ് ചെയ്തു!',
  cert_download_failed: 'സർട്ടിഫിക്കറ്റ് സൃഷ്ടിക്കുന്നതിൽ പരാജയപ്പെട്ടു. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
  course_fallback: 'കോഴ്സ്.',
  no_courses_enrolled: 'ഇതുവരെ കോഴ്‌സുകളൊന്നും എൻറോൾ ചെയ്തിട്ടില്ല',
  no_courses_enrolled_desc: 'എൻറോൾ ചെയ്ത കോഴ്സുകൾ ഇവിടെ ദൃശ്യമാകും, അതിനാൽ നിങ്ങൾക്ക് പഠനം തുടരാം',
  continue_learning: 'പഠനം തുടരുക',
  modules_label: 'മൊഡ്യൂളുകൾ',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'എൻറോൾ ചെയ്തു',
  enrollment_failed: 'എൻറോൾമെൻ്റ് പരാജയപ്പെട്ടു.',
  view_course: 'കോഴ്സ് കാണുക.',
  kpi_need_80_certify: 'സാക്ഷ്യപ്പെടുത്താൻ കുറഞ്ഞത് 80 ശതമാനം.',
  kpi_no_attempts_yet: 'ഇതുവരെ ശ്രമങ്ങളൊന്നുമില്ല',
  courses_pending_suffix: 'കോഴ്സുകൾ തീർപ്പാക്കിയിട്ടില്ല',
  module_marked_complete: 'മൊഡ്യൂൾ പൂർത്തിയായതായി അടയാളപ്പെടുത്തി!',
  failed_update_progress: 'പുരോഗതി അപ്‌ഡേറ്റ് ചെയ്യുന്നതിൽ പരാജയപ്പെട്ടു.',
  no_downloadable_content: 'ഈ മൊഡ്യൂളിനായി ഡൗൺലോഡ് ചെയ്യാവുന്ന ഉള്ളടക്കമൊന്നും ലഭ്യമല്ല',
  download_started: 'ഡൗൺലോഡ് ആരംഭിച്ചു!',
  download_failed_retry: 'ഡൗൺലോഡ് പരാജയപ്പെട്ടു. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
  enrolled_loading_course: 'എൻറോൾ ചെയ്തു! കോഴ്സ് ലോഡ് ചെയ്യുന്നു',
  topic_quiz_passed_toast: 'വിഷയ ക്വിസ് വിജയിച്ചു!',
  score_saved_prefix: 'സ്കോർ സംരക്ഷിച്ചു.',
  need_80_to_pass: 'വിജയിക്കാൻ 80 ശതമാനമെങ്കിലും വേണം.',
  failed_save_quiz_results: 'ക്വിസ് ഫലങ്ങൾ സംരക്ഷിക്കുന്നതിൽ പരാജയപ്പെട്ടു',
  score_saved_cert_generated: 'സ്കോർ സംരക്ഷിച്ചു! ഇതിനായി സർട്ടിഫിക്കറ്റ് സൃഷ്‌ടിച്ചു',
  need_80_for_cert: 'സർട്ടിഫിക്കറ്റിന് 80 ശതമാനമെങ്കിലും വേണം.',
  no_course_selected: 'കോഴ്‌സൊന്നും തിരഞ്ഞെടുത്തിട്ടില്ല. തിരികെ പോയി ഒരു കോഴ്‌സ് തിരഞ്ഞെടുക്കുക.',
  back_to_dashboard: 'ഡാഷ്‌ബോർഡിലേക്ക് മടങ്ങുക',
  need_enroll_message: 'ഈ കോഴ്‌സിൻ്റെ വീഡിയോകൾ കാണുന്നതിന് മുമ്പോ അതിൻ്റെ ക്വിസ് എടുക്കുന്നതിന് മുമ്പ് നിങ്ങൾ അതിൽ എൻറോൾ ചെയ്യേണ്ടതുണ്ട്.',
  lessons_completed_suffix: 'പാഠങ്ങൾ പൂർത്തിയാക്കി',
  no_lessons_uploaded: 'ഇതുവരെ പാഠങ്ങളൊന്നും അപ്‌ലോഡ് ചെയ്‌തിട്ടില്ല',
  min_suffix: 'മിനിറ്റ്',
  no_media_uploaded: 'ഈ പാഠത്തിനായി ഒരു മീഡിയയും അപ്‌ലോഡ് ചെയ്‌തിട്ടില്ല',
  admin_trainer_upload_hint: 'കോഴ്‌സ് മാനേജ്‌മെൻ്റ് പാനലിൽ നിന്ന് അഡ്മിൻ/ട്രെയിനർക്ക് വീഡിയോ/പിഡിഎഫ് അപ്‌ലോഡ് ചെയ്യാം',
  select_module_to_start: 'പഠനം ആരംഭിക്കാൻ ഒരു മൊഡ്യൂൾ തിരഞ്ഞെടുക്കുക',
  video_label: 'വീഡിയോ',
  pdf_label: 'PDF.',
  browser_no_video_support: 'നിങ്ങളുടെ ബ്രൗസർ വീഡിയോ ടാഗിനെ പിന്തുണയ്ക്കുന്നില്ല.',
  profile_email: 'ഇമെയിൽ',
  profile_full_name: 'പൂർണ്ണമായ പേര്',
  profile_phone: 'ഫോൺ',
  profile_village: 'ഗ്രാമം',
  profile_district: 'ജില്ല',
  profile_age: 'പ്രായം',
  profile_gender: 'ലിംഗഭേദം',
  profile_city: 'നഗരം',
  profile_language: 'ഭാഷ.',
  profile_updated_toast: 'പ്രൊഫൈൽ അപ്ഡേറ്റ് ചെയ്തു',
  profile_update_failed: 'പ്രൊഫൈൽ അപ്ഡേറ്റ് ചെയ്യുന്നതിൽ പരാജയപ്പെട്ടു.',
  welcome_back_signing_in_as: 'തിരികെ സ്വാഗതം! നിങ്ങളെ ഇതായി സൈൻ ഇൻ ചെയ്യുന്നു',
  account_created_signing_in: 'അക്കൗണ്ട് സൃഷ്ടിച്ചു! നിങ്ങളെ സൈൻ ഇൻ ചെയ്യുന്നു.',
  authentication_failed: 'പ്രാമാണീകരണം പരാജയപ്പെട്ടു',
  invalid_credentials_use_demo: 'അസാധുവായ ഇമെയിൽ അല്ലെങ്കിൽ പാസ്‌വേഡ്. ചുവടെയുള്ള ഡെമോ അക്കൗണ്ടുകൾ ഉപയോഗിക്കുക.',
  email_already_registered: 'This email is already registered. Please sign in instead.',
  topic_otp_fraud: 'OTP തട്ടിപ്പ്.',
  topic_upi_safety: 'UPI സുരക്ഷ',
  topic_loan_fraud: 'വായ്പ തട്ടിപ്പ്',
  topic_cyber_safety: 'സൈബർ സുരക്ഷ.',
  topic_kyc_basics: 'KYC അടിസ്ഥാനങ്ങൾ.',
  topic_digital_banking: 'ഡിജിറ്റൽ ബാങ്കിംഗ്.',
  placeholder_min_8_chars: 'കുറഞ്ഞത് 8 പ്രതീകങ്ങൾ',
  placeholder_your_password: 'നിങ്ങളുടെ പാസ്‌വേഡ്',
  password_min_length_error: 'Password must be at least 8 characters',
  password_required_error: 'പാസ്‌വേഡ് ആവശ്യമാണ്.',
  placeholder_age_example: 'ഉദാ. 28',
  placeholder_your_city: 'നിങ്ങളുടെ നഗരം.',
  placeholder_mobile_number: '10 അക്ക മൊബൈൽ നമ്പർ',
  select_placeholder: 'തിരഞ്ഞെടുക്കുക',
  gender_male: 'പുരുഷൻ.',
  gender_female: 'സ്ത്രീ.',
  gender_other: 'മറ്റുള്ളവ.',
  quiz_results_save_error: 'നിങ്ങളുടെ ക്വിസ് ഫലങ്ങൾ സംരക്ഷിക്കുന്നതിൽ എന്തോ കുഴപ്പം സംഭവിച്ചു.',
  signed_out_success: 'വിജയകരമായി സൈൻ ഔട്ട് ചെയ്‌തു.',
  sign_out_failed: 'സൈൻ ഔട്ട് പരാജയപ്പെട്ടു',
  expand_sidebar: 'സൈഡ്‌ബാർ വികസിപ്പിക്കുക.',
  collapse_sidebar: 'സൈഡ്‌ബാർ ചുരുക്കുക',
  switch_to_light_mode: 'ലൈറ്റ് മോഡിലേക്ക് മാറുക',
  switch_to_dark_mode: 'ഡാർക്ക് മോഡിലേക്ക് മാറുക',


  // Signup form additions
  placeholder_your_full_name: 'നിങ്ങളുടെ മുഴുവൻ പേര്',
  mobile_number_label: 'മൊബൈൽ നമ്പർ',
  placeholder_email_example: 'you@example.com',
  copy_password_tooltip: 'പാസ്‌വേഡ് പകർത്തുക.',


  // Admin dashboard — our web properties
  our_properties_title: 'ഞങ്ങളുടെ വെബ് പ്രോപ്പർട്ടികൾ',

  property_main_site_label: 'ഫീഡ് ഫൗണ്ടേഷൻ',
  property_main_site_full_title: 'സമത്വത്തിനും തുല്യതയ്ക്കും അന്തസ്സിനുമുള്ള അടിത്തറ',

  property_flc_site_label: 'CMS-FEED FLC',
  property_flc_site_full_title: 'CMS-FEED സാമ്പത്തിക സാക്ഷരതാ കേന്ദ്രം',

  login_email_or_phone: 'ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ',
  placeholder_email_or_phone: 'you@example.com അല്ലെങ്കിൽ 10-അക്ക നമ്പർ',
  login_email_required_error: 'ഇമെയിൽ ആവശ്യമാണ്',
  login_identifier_required_error: 'ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ ആവശ്യമാണ്',

  phone_invalid_error: 'സാധുവായ 10 അക്ക മൊബൈൽ നമ്പർ നൽകുക',
  phone_required_error: 'മൊബൈൽ നമ്പർ ആവശ്യമാണ്',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'എൻറോൾ ചെയ്തു',
  login_forgot_password: 'പാസ്‌വേഡ് മറന്നോ?',
  fp_title: 'പാസ്‌വേഡ് മറന്നു',
  fp_subtitle: 'നിങ്ങളുടെ ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ നൽകുക, ഞങ്ങൾ 6 അക്ക OTP ഇമെയിൽ ചെയ്യും.',
  fp_identifier_placeholder: 'ഇമെയിൽ അല്ലെങ്കിൽ മൊബൈൽ നമ്പർ',
  fp_send_otp: 'OTP അയയ്ക്കുക',
  fp_otp_sent: 'അക്കൗണ്ട് ഉണ്ടെങ്കിൽ അതിന്റെ ഇമെയിലിലേക്ക് OTP അയച്ചിട്ടുണ്ട്. പുതിയ പാസ്‌വേഡിനൊപ്പം താഴെ നൽകുക. OTP 10 മിനിറ്റ് സാധുവാണ്.',
  fp_otp_placeholder: '6 അക്ക OTP',
  fp_new_password: 'പുതിയ പാസ്‌വേഡ് (കുറഞ്ഞത് 8 അക്ഷരങ്ങൾ)',
  fp_confirm_password: 'പുതിയ പാസ്‌വേഡ് സ്ഥിരീകരിക്കുക',
  fp_reset_password: 'പാസ്‌വേഡ് റീസെറ്റ് ചെയ്യുക',
  fp_resend_otp: 'OTP വീണ്ടും അയയ്ക്കുക',
  fp_done: 'നിങ്ങളുടെ പാസ്‌വേഡ് അപ്ഡേറ്റ് ചെയ്തു.',
  fp_pw_min: 'പാസ്‌വേഡ് കുറഞ്ഞത് 8 അക്ഷരങ്ങൾ ആയിരിക്കണം.',
  fp_pw_mismatch: 'പാസ്‌വേഡുകൾ പൊരുത്തപ്പെടുന്നില്ല.',
  fp_back_to_signin: 'സൈൻ ഇനിലേക്ക് മടങ്ങുക',
  not_found_title: 'പേജ് കണ്ടെത്തിയില്ല',
  not_found_desc: 'നിങ്ങൾ തിരയുന്ന പേജ് നിലവിലില്ല. നമുക്ക് തിരികെ പോകാം!',
  go_back: 'തിരികെ പോകുക',
  back_to_home: 'ഹോമിലേക്ക് മടങ്ങുക',
  trainer_my_courses: 'എന്റെ കോഴ്‌സുകൾ',
  trainer_courses_by_you: 'നിങ്ങൾ സൃഷ്ടിച്ച കോഴ്‌സ്(കൾ)',
  trainer_add_course: 'കോഴ്‌സ് ചേർക്കുക',
  trainer_modules_count: 'മൊഡ്യൂൾ(കൾ)',
  trainer_manage_modules: 'വീഡിയോ/മൊഡ്യൂളുകൾ നിയന്ത്രിക്കുക',
  trainer_edit_course: 'കോഴ്‌സ് എഡിറ്റ് ചെയ്യുക',
  trainer_delete_course: 'കോഴ്‌സ് ഇല്ലാതാക്കുക',
  trainer_completion_overview: 'കോഴ്‌സ് പൂർത്തീകരണ അവലോകനം',
  login_initiative: 'FEED — സമത്വം, തുല്യത, അന്തസ്സ് എന്നിവയ്ക്കുള്ള ഫൗണ്ടേഷൻ & CMS ഫൗണ്ടേഷന്റെ സംയുക്ത സംരംഭം',
};

const mr: Dict = {
  save: 'जतन करा', save_changes: 'बदल जतन करा', cancel: 'रद्द करा', delete: 'हटवा', edit: 'संपादित करा',
  add: 'जोडा', search: 'शोधा', close: 'बंद करा', loading: 'लोड होत आहे…', saving: 'जतन होत आहे…',
  refresh: 'रिफ्रेश करा', back: 'मागे', continue: 'सुरू ठेवा', enroll: 'नोंदणी करा', enrolling: 'नोंदणी होत आहे…',
  download: 'डाउनलोड', sign_out: 'साइन आउट', signing_out: 'साइन आउट होत आहे…', my_profile: 'माझी प्रोफाइल',
  settings: 'सेटिंग्ज', nav_dashboard: 'डॅशबोर्ड', nav_reports: 'अहवाल', nav_courses: 'अभ्यासक्रम',
  nav_course_management: 'अभ्यासक्रम व्यवस्थापन', nav_media: 'व्हिडिओ आणि मीडिया', nav_assessments: 'मूल्यांकन',
  nav_members: 'सदस्य', nav_trainers: 'प्रशिक्षक', nav_certificates: 'प्रमाणपत्रे', nav_notifications: 'सूचना',
  nav_settings: 'सेटिंग्ज', nav_learner_progress: 'शिकणाऱ्याची प्रगती', nav_my_dashboard: 'माझा डॅशबोर्ड',
  nav_my_courses: 'माझे अभ्यासक्रम', nav_help: 'मदत', group_overview: 'आढावा', group_content: 'सामग्री',
  group_people: 'लोक', group_system: 'सिस्टम', group_my_work: 'माझे काम', group_learning: 'शिकणे', group_support: 'सहाय्य',
  role_admin: 'अ‍ॅडमिन — FEED HQ', role_trainer: 'फील्ड प्रशिक्षक', role_learner: 'शिकणारा',
  login_welcome_back: 'पुन्हा स्वागत आहे', login_create_account: 'खाते तयार करा',
  login_signin_subtitle: 'तुमचा आर्थिक साक्षरता प्रवास सुरू ठेवण्यासाठी साइन इन करा',
  login_signup_subtitle: 'RBI केहता है प्रशिक्षण कार्यक्रमात सामील व्हा', login_i_am_a: 'मी आहे',
  login_role_learner: 'शिकणारा', login_role_trainer: 'फील्ड प्रशिक्षक', login_role_admin: 'अ‍ॅडमिन',
  login_full_name: 'पूर्ण नाव', login_email: 'ईमेल पत्ता', login_password: 'पासवर्ड', login_sign_in: 'साइन इन करा',
  login_sign_up: 'खाते तयार करा', login_signing_in: 'साइन इन होत आहे…', login_creating_account: 'खाते तयार होत आहे…',
  login_no_account: 'खाते नाही? ', login_have_account: 'आधीच खाते आहे? ', login_toggle_signup: 'साइन अप करा',
  login_toggle_signin: 'साइन इन करा', login_demo_accounts: '🔒 डेमो खाती — भरण्यासाठी क्लिक करा',
  login_hero_tagline: 'RBI केहता है मोहीम',
  login_hero_desc: 'शिकणारे, समुदाय सदस्य आणि फील्ड कर्मचाऱ्यांसाठी आर्थिक साक्षरता प्रशिक्षण — OTP फसवणूक, UPI सुरक्षा, कर्ज फसवणूक आणि सायबर सुरक्षा.',
  admin_dashboard_title: 'प्रशिक्षण परिणाम डॅशबोर्ड', admin_dashboard_subtitle: 'RBI केहता है — FEED आणि CMS फाउंडेशन',
  export_report: 'अहवाल निर्यात करा', filter: 'फिल्टर', this_week: 'या आठवड्यात', this_month: 'या महिन्यात',
  last_3_months: 'गेले 3 महिने', all_time: 'सर्व काळ', kpi_members_trained: 'प्रशिक्षित सदस्य',
  kpi_members_trained_sub: 'एकूण नोंदणीकृत शिकणारे', kpi_certificates_issued: 'जारी केलेली प्रमाणपत्रे',
  kpi_all_time: 'सर्व काळ', kpi_members_pending: 'प्रलंबित सदस्य', kpi_members_pending_sub: 'अद्याप नोंदणी केलेली नाही',
  kpi_pass_rate: 'एकूण उत्तीर्ण दर', kpi_districts_covered: 'समाविष्ट जिल्हे', kpi_districts_covered_sub: 'सर्व शिकणाऱ्यांमध्ये',
  kpi_active_courses: 'सक्रिय अभ्यासक्रम', kpi_active_courses_sub: 'प्रकाशित आणि उपलब्ध',
  trainer_dashboard_title: 'प्रशिक्षक डॅशबोर्ड', trainer_courses_created: 'तयार केलेले अभ्यासक्रम',
  trainer_total_learners: 'एकूण शिकणारे', trainer_avg_completion: 'सरासरी पूर्णता', trainer_certs_issued: 'जारी केलेली प्रमाणपत्रे',
  learner_namaste: 'नमस्ते', learner_dashboard_subtitle: 'तुमचे RBI केहता है आर्थिक साक्षरता प्रशिक्षण सुरू ठेवा',
  learner_courses_enrolled: 'नोंदणीकृत अभ्यासक्रम', learner_modules_completed: 'पूर्ण मॉड्यूल्स', learner_avg_quiz_score: 'सरासरी क्विझ स्कोअर',
  learner_certificates_earned: 'मिळवलेली प्रमाणपत्रे', my_courses_title: 'माझे अभ्यासक्रम',
  my_courses_subtitle: 'जिथे थांबला होता तिथून सुरू ठेवा, किंवा खाली नवीन अभ्यासक्रमात नोंदणी करा',
  browse_courses: 'अभ्यासक्रम पहा', continue_course: 'अभ्यासक्रम सुरू ठेवा', take_final_quiz: 'अंतिम क्विझ द्या',
  lessons: 'धडे', topics: 'विषय', course_progress: 'अभ्यासक्रम प्रगती', mark_complete: 'पूर्ण म्हणून चिन्हांकित करा',
  completed: 'पूर्ण झाले', previous: 'मागील', next: 'पुढील', enroll_now: 'आता नोंदणी करा',
  take_topic_quiz: 'विषय क्विझ द्या', topic_quiz_passed: 'क्विझ उत्तीर्ण',

  // Quiz modal, Certificates panel, Active module player (auto-translated)
  quiz_loading: 'क्विझ लोड करत आहे',
  quiz_fetching_questions: 'कोर्समधून प्रश्न आणत आहे',
  quiz_title: 'प्रश्नमंजुषा',
  quiz_no_questions_course: 'या कोर्ससाठी अद्याप कोणतेही क्विझ प्रश्न उपलब्ध नाहीत.',
  quiz_no_questions_topic: 'या विषयासाठी अद्याप कोणतेही क्विझ प्रश्न उपलब्ध नाहीत.',
  quiz_results_title: 'क्विझ परिणाम',
  quiz_question_prefix: 'प्रश्नमंजुषा, प्रश्न.',
  of: 'च्या',
  financial_literacy_quiz: 'आर्थिक साक्षरता प्रश्नमंजुषा',
  show_hint: 'इशारा दाखवा.',
  hide_explanation: 'स्पष्टीकरण लपवा.',
  submit_quiz: 'क्विझ सबमिट करा',
  select_answer_warning: 'कृपया सुरू ठेवण्यापूर्वी उत्तर निवडा',
  you_answered: 'तुम्ही उत्तर दिले',
  out_of: 'च्या बाहेर',
  correctly: 'बरोबर',
  quiz_congrats: 'अभिनंदन! तुम्ही पास झालात!',
  quiz_cert_generated_note: 'तुमचे प्रमाणपत्र तयार केले गेले आहे. प्रमाणपत्र पॅनेलवरून ते डाउनलोड करा.',
  quiz_need_80: 'प्रमाणपत्र मिळविण्यासाठी तुम्हाला किमान 80 टक्के हवे आहेत',
  quiz_review_retry: 'सामग्रीचे पुनरावलोकन करा आणि तुमचे प्रमाणपत्र मिळविण्यासाठी पुन्हा प्रयत्न करा.',
  correct_answer_label: 'बरोबर',
  retry: 'पुन्हा प्रयत्न करा.',
  done: 'झाले.',
  my_certificates: 'माझी प्रमाणपत्रे.',
  pending_certificates: 'प्रलंबित प्रमाणपत्रे.',
  download_pdf: 'PDF डाउनलोड करा',
  generating: 'निर्माण करत आहे',
  no_certificates_yet: 'अद्याप कोणतेही प्रमाणपत्र नाही.',
  score_80_hint: 'एक मिळवण्यासाठी क्विझवर किमान 80 टक्के गुण मिळवा',
  not_attempted: 'प्रयत्न केला नाही.',
  need_label: 'गरज आहे.',
  ready_label: 'तयार!',
  score_label: 'स्कोअर',
  certificate_for: 'साठी प्रमाणपत्र',
  downloaded_suffix: 'डाउनलोड केले!',
  cert_download_failed: 'प्रमाणपत्र व्युत्पन्न करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
  course_fallback: 'अभ्यासक्रम',
  no_courses_enrolled: 'अद्याप कोणत्याही अभ्यासक्रमांची नोंदणी झालेली नाही.',
  no_courses_enrolled_desc: 'नोंदणी केलेले अभ्यासक्रम येथे दिसतील जेणेकरून तुम्ही शिकणे सुरू ठेवू शकता',
  continue_learning: 'शिकत राहा',
  modules_label: 'मॉड्यूल्स',


  // Course content, profile settings, login/signup, global chrome (auto-translated)
  enrolled_in_prefix: 'मध्ये नोंदणी केली',
  enrollment_failed: 'नावनोंदणी अयशस्वी.',
  view_course: 'कोर्स पहा.',
  kpi_need_80_certify: 'प्रमाणित करण्यासाठी किमान 80 टक्के',
  kpi_no_attempts_yet: 'अजून प्रयत्न नाहीत.',
  courses_pending_suffix: 'अभ्यासक्रम प्रलंबित',
  module_marked_complete: 'मॉड्यूल पूर्ण म्हणून चिन्हांकित केले!',
  failed_update_progress: 'प्रगती अपडेट करण्यात अयशस्वी.',
  no_downloadable_content: 'या मॉड्यूलसाठी कोणतीही डाउनलोड करण्यायोग्य सामग्री उपलब्ध नाही',
  download_started: 'डाउनलोड सुरू झाले!',
  download_failed_retry: 'डाउनलोड अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
  enrolled_loading_course: 'नोंदणी केली! अभ्यासक्रम लोड करत आहे',
  topic_quiz_passed_toast: 'विषयावरील प्रश्नमंजुषा उत्तीर्ण!',
  score_saved_prefix: 'स्कोअर सेव्ह केला.',
  need_80_to_pass: 'उत्तीर्ण होण्यासाठी किमान 80 टक्के गुण हवेत.',
  failed_save_quiz_results: 'क्विझ निकाल जतन करण्यात अयशस्वी',
  score_saved_cert_generated: 'स्कोअर जतन केला! साठी प्रमाणपत्र व्युत्पन्न केले',
  need_80_for_cert: 'प्रमाणपत्रासाठी किमान 80 टक्के आवश्यक आहे.',
  no_course_selected: 'कोणताही अभ्यासक्रम निवडलेला नाही. कृपया परत जा आणि एक कोर्स निवडा.',
  back_to_dashboard: 'डॅशबोर्डवर परत',
  need_enroll_message: 'तुम्ही या कोर्सचे व्हिडिओ पाहण्यापूर्वी किंवा त्याची क्विझ घेण्यापूर्वी तुम्हाला या कोर्समध्ये नावनोंदणी करणे आवश्यक आहे.',
  lessons_completed_suffix: 'धडे पूर्ण केले',
  no_lessons_uploaded: 'अद्याप कोणतेही धडे अपलोड केलेले नाहीत',
  min_suffix: 'मि',
  no_media_uploaded: 'या धड्यासाठी कोणतेही माध्यम अपलोड केलेले नाही',
  admin_trainer_upload_hint: 'प्रशासन/प्रशिक्षक कोर्स मॅनेजमेंट पॅनलमधून व्हिडिओ/पीडीएफ अपलोड करू शकतात.',
  select_module_to_start: 'शिकणे सुरू करण्यासाठी मॉड्यूल निवडा',
  video_label: 'व्हिडिओ',
  pdf_label: 'PDF.',
  browser_no_video_support: 'तुमचा ब्राउझर व्हिडिओ टॅगला सपोर्ट करत नाही.',
  profile_email: 'ईमेल.',
  profile_full_name: 'पूर्ण नाव.',
  profile_phone: 'फोन',
  profile_village: 'गाव.',
  profile_district: 'जिल्हा',
  profile_age: 'वय',
  profile_gender: 'लिंग',
  profile_city: 'शहर',
  profile_language: 'भाषा',
  profile_updated_toast: 'प्रोफाइल अपडेट केले',
  profile_update_failed: 'प्रोफाइल अपडेट करण्यात अयशस्वी',
  welcome_back_signing_in_as: 'परत स्वागत आहे! म्हणून तुम्हाला साइन इन करत आहे.',
  account_created_signing_in: 'खाते तयार केले! तुम्हाला साइन इन करत आहे',
  authentication_failed: 'प्रमाणीकरण अयशस्वी',
  invalid_credentials_use_demo: 'अवैध ईमेल किंवा पासवर्ड. खालील डेमो खाती वापरा.',
  email_already_registered: 'हा ईमेल आधीच नोंदणीकृत आहे. त्याऐवजी कृपया साइन इन करा.',
  topic_otp_fraud: 'OTP फसवणूक',
  topic_upi_safety: 'UPI सुरक्षितता',
  topic_loan_fraud: 'कर्ज फसवणूक.',
  topic_cyber_safety: 'सायबर सुरक्षा.',
  topic_kyc_basics: 'केवायसी मूलभूत',
  topic_digital_banking: 'डिजिटल बँकिंग.',
  placeholder_min_8_chars: 'किमान ८ वर्ण',
  placeholder_your_password: 'तुमचा पासवर्ड',
  password_min_length_error: 'पासवर्ड किमान 8 वर्णांचा असणे आवश्यक आहे',
  password_required_error: 'पासवर्ड आवश्यक आहे',
  placeholder_age_example: 'उदा. २८',
  placeholder_your_city: 'आपले शहर.',
  placeholder_mobile_number: '10-अंकी मोबाइल नंबर',
  select_placeholder: 'निवडा',
  gender_male: 'पुरुष',
  gender_female: 'स्त्री',
  gender_other: 'इतर',
  quiz_results_save_error: 'तुमचे प्रश्नमंजुषा निकाल जतन करताना काहीतरी चूक झाली.',
  signed_out_success: 'यशस्वीरित्या साइन आउट केले',
  sign_out_failed: 'साइन आउट अयशस्वी.',
  expand_sidebar: 'साइडबार विस्तृत करा.',
  collapse_sidebar: 'साइडबार संकुचित करा.',
  switch_to_light_mode: 'लाईट मोडवर स्विच करा',
  switch_to_dark_mode: 'गडद मोडवर स्विच करा.',


  // Signup form additions
  placeholder_your_full_name: 'तुमचे पूर्ण नाव',
  mobile_number_label: 'मोबाईल नंबर.',
  placeholder_email_example: 'you@example.com.',
  copy_password_tooltip: 'पासवर्ड कॉपी करा.',


  // Admin dashboard — our web properties
  our_properties_title: 'आमचे वेब गुणधर्म',

  property_main_site_label: 'फीड फाउंडेशन',
  property_main_site_full_title: 'समानता, समानता आणि सन्मानासाठी पाया',

  property_flc_site_label: 'CMS-FEED FLC.',
  property_flc_site_full_title: 'CMS-FEED आर्थिक साक्षरता केंद्र',

  login_email_or_phone: 'ईमेल किंवा मोबाईल नंबर',
  placeholder_email_or_phone: 'you@example.com किंवा 10-अंकी क्रमांक',
  login_email_required_error: 'ईमेल आवश्यक आहे',
  login_identifier_required_error: 'ईमेल किंवा मोबाईल नंबर आवश्यक आहे',

  phone_invalid_error: 'एक वैध 10-अंकी मोबाइल नंबर प्रविष्ट करा.',
  phone_required_error: 'मोबाईल नंबर आवश्यक आहे.',

  // Added: previously missing / newly extracted strings
  status_enrolled: 'नोंदणी झाली',
  login_forgot_password: 'पासवर्ड विसरलात?',
  fp_title: 'पासवर्ड विसरलात',
  fp_subtitle: 'तुमचा ईमेल किंवा मोबाइल नंबर टाका, आम्ही तुम्हाला 6 अंकी OTP ईमेल करू.',
  fp_identifier_placeholder: 'ईमेल किंवा मोबाइल नंबर',
  fp_send_otp: 'OTP पाठवा',
  fp_otp_sent: 'खाते असल्यास त्याच्या ईमेलवर OTP पाठवला आहे. नवीन पासवर्डसह तो खाली टाका. OTP 10 मिनिटे वैध आहे.',
  fp_otp_placeholder: '6 अंकी OTP',
  fp_new_password: 'नवीन पासवर्ड (किमान 8 अक्षरे)',
  fp_confirm_password: 'नवीन पासवर्डची पुष्टी करा',
  fp_reset_password: 'पासवर्ड रीसेट करा',
  fp_resend_otp: 'OTP पुन्हा पाठवा',
  fp_done: 'तुमचा पासवर्ड अपडेट झाला आहे.',
  fp_pw_min: 'पासवर्ड किमान 8 अक्षरांचा असावा.',
  fp_pw_mismatch: 'पासवर्ड जुळत नाहीत.',
  fp_back_to_signin: 'साइन इनवर परत जा',
  not_found_title: 'पृष्ठ सापडले नाही',
  not_found_desc: 'तुम्ही शोधत असलेले पृष्ठ अस्तित्वात नाही. चला तुम्हाला परत घेऊन जाऊ!',
  go_back: 'मागे जा',
  back_to_home: 'मुख्यपृष्ठावर परत जा',
  trainer_my_courses: 'माझे अभ्यासक्रम',
  trainer_courses_by_you: 'तुम्ही तयार केलेले अभ्यासक्रम',
  trainer_add_course: 'अभ्यासक्रम जोडा',
  trainer_modules_count: 'मॉड्यूल',
  trainer_manage_modules: 'व्हिडिओ/मॉड्यूल व्यवस्थापित करा',
  trainer_edit_course: 'अभ्यासक्रम संपादित करा',
  trainer_delete_course: 'अभ्यासक्रम हटवा',
  trainer_completion_overview: 'अभ्यासक्रम पूर्णता आढावा',
  login_initiative: 'FEED — समानता, न्याय आणि सन्मानासाठी फाउंडेशन आणि CMS फाउंडेशन यांचा संयुक्त उपक्रम',
};

export const translations: Record<LanguageCode, Dict> = { en, hi, te, ta, kn, bn, ml, mr };
